import { friendlyDbError } from '../utils/friendlyDbError';
import { create } from 'zustand';
import { supabase } from '../supabase';
import type { Deal, NewDeal, DealServiceFormData, DealNote } from '../types';
import { triggerWhatsAppAutomation } from '../utils/whatsapp';
import { useCalendarStore } from './calendarStore';
import { getUserData } from '../utils';
import { contractRenewalService } from '../services/contractRenewalService';
import { eventBus } from '../events/eventBus';
import { dispatchTaskCompleted } from '../onboarding/dispatchTaskCompleted';
import type { UserFilterSelection } from '../../contexts/DynamicHeaderContext';

export interface GlobalDealSearchResult {
  id: string;
  title: string | null;
  status: string | null;
  created_at: string | null;
  amount: number | null;
  client_id: string | null;
  client_business_name: string | null;
}

interface DealStore {
  deals: Deal[];
  loading: boolean;
  statusUpdateLoading: boolean;
  error: string | null;

  // User filter - supports multiple user selection
  selectedUserId: UserFilterSelection[] | null;
  setSelectedUser: (userId: UserFilterSelection[] | null) => void;

  fetchDeals: () => Promise<void>;
  fetchAllDeals: () => Promise<void>; // Fetch all deals without pagination
  fetchDealById: (id: string, force?: boolean) => Promise<Deal | null>;
  /**
   * Ricerca globale lato server: ignora tutti i filtri client (periodo, status,
   * probabilità, user filter) e cerca per `title`/`description` deal +
   * `business_name` cliente tra TUTTI i deal dell'org corrente. Pensata per il
   * fallback "non trovo in lista perché ho dei filtri attivi": ritorna fino a
   * `limit` risultati leggeri usati per popolare un banner cliccabile sopra
   * alla lista filtrata.
   */
  searchDealsGlobal: (query: string, limit?: number) => Promise<GlobalDealSearchResult[]>;
  createDeal: (deal: NewDeal, services: DealServiceFormData[], contactIds?: string[], opts?: { skipWorkflows?: boolean }) => Promise<Deal | null>;
  updateDeal: (id: string, deal: Partial<Deal>, services?: DealServiceFormData[], contactIds?: string[]) => Promise<Deal | null>;
  /**
   * Elimina l'offerta. Ordini di magazzino collegati: scollegati con una nota
   * interna; con `deleteDraftOrders` quelli in bozza vengono eliminati.
   * Restituisce i numeri degli ordini in bozza che non è stato possibile eliminare.
   */
  deleteDeal: (id: string, deleteAssociatedProjects?: boolean, options?: { deleteDraftOrders?: boolean }) => Promise<string[]>;
  getAssociatedProjects: (dealId: string) => Promise<any[]>;
  handleDealSigned: (dealId: string) => Promise<void>;
  downloadSignedDealPDF: (deal: Deal) => Promise<void>;
  /**
   * `options.skipNote`: non creare la nota `status_change` anche se `note` è valorizzata.
   * Serve a chi la nota se la scrive da sé con gli allegati (drawer Attività/Nota del deal,
   * `dealDrawerAdapter.ts`): qui la nota veniva creata SENZA allegati, che andavano persi.
   * Parametro additivo — i call site esistenti non cambiano comportamento.
   */
  updateDealStatus: (id: string, status: string, note?: string, options?: { skipNote?: boolean }) => Promise<Deal | null>;
  fetchDealNotes: (dealId: string) => Promise<DealNote[]>;
  fetchDealActivities: (dealId: string) => Promise<any[]>;
  addDealNote: (dealId: string, content: string, metadata?: any) => Promise<DealNote | null>;
  updateDealNote: (noteId: string, content: string) => Promise<DealNote | null>;
  deleteDealNote: (noteId: string, dealId: string) => Promise<boolean>;
  deleteDealActivity: (activityId: string, dealId: string) => Promise<boolean>;
  fetchNoteVersions: (noteId: string) => Promise<DealNoteVersion[]>;
  
  // Contract renewal methods
  getExpiringContracts: (daysAhead?: number) => Promise<any[]>;
  updateContractRenewal: (dealServiceId: string, renewalData: any, dealId?: string) => Promise<boolean>;
  createRenewalTasks: () => Promise<number>;
  duplicateDeal: (dealId: string, customTitle?: string) => Promise<Deal | null>;
  
  // Cost breakdown calculations
  calculateDealTotalWithCosts: (dealId: string) => Promise<{ serviceTotal: number; costTotal: number; grandTotal: number; } | null>;
  calculateDealTotalsDetailed: (dealId: string) => Promise<{ clientTotal: number; internalTotal: number; marginAmount: number; marginPercentage: number; } | null>;
  getDealCostBreakdown: (dealId: string) => Promise<any>;
  recalculateDealAmountWithCosts: (dealId: string) => Promise<boolean>;

  // Deal suggestions from WhatsApp AI
  createDealFromSuggestion: (suggestionId: string) => Promise<Deal | null>;

  // Hours package management (monte ore)
  updateHoursPackage: (dealId: string, hoursPackage: number, alertThreshold?: number, hourlyRate?: number) => Promise<boolean>;
  getHoursUsagePercent: (dealId: string) => number | null;
  checkHoursAlert: (dealId: string) => { isAlert: boolean; percentUsed: number; hoursRemaining: number } | null;
  addHoursToPackage: (dealId: string, additionalHours: number) => Promise<boolean>;
}

// ─── Helper: persiste le selezioni add-on di una riga deal_service ──────────
// Snapshot pattern: scriviamo name/description/amount/is_required/is_included_in_base
// direttamente in deal_service_addons cosi le modifiche future al catalogo
// service_addons non alterano i deal storici (vedi Plan B).
async function persistDealServiceAddons(
  dealServiceId: string,
  organizationId: string,
  addons: NonNullable<DealServiceFormData['addons']> | undefined,
): Promise<void> {
  if (!addons || addons.length === 0) return;
  const rows = addons.map(a => ({
    organization_id: organizationId,
    deal_service_id: dealServiceId,
    service_addon_id: a.service_addon_id ?? null,
    name: a.name,
    description: a.description ?? null,
    amount: Number(a.amount ?? 0),
    is_required: !!a.is_required,
    is_included_in_base: !!a.is_included_in_base,
  }));
  const { error } = await supabase.from('deal_service_addons').insert(rows);
  if (error) {
    // Soft-fail: log + continue. Non blocchiamo il save del deal per un addon che non si salva.
    console.error('[persistDealServiceAddons] insert error:', error.message, { dealServiceId, rowsCount: rows.length });
  }
}

export const useDealStore = create<DealStore>((set, get) => ({
  deals: [],
  loading: false,
  statusUpdateLoading: false,
  error: null,
  
  // User filter - supports multiple user selection
  selectedUserId: null,
  setSelectedUser: (userSelection) => {
    const previousSelection = get().selectedUserId;

    // Evita update se identico
    if (JSON.stringify(previousSelection) === JSON.stringify(userSelection)) {
      return;
    }

    console.log(`[DealStore] setSelectedUser called:`, {
      newSelection: userSelection,
      previousSelection,
      timestamp: new Date().toISOString()
    });

    set({ selectedUserId: userSelection });
    // NOTA: Il fetch sarà gestito dal componente DealsPage
  },
  
  fetchDealNotes: async (dealId: string) => {
    try {
      console.log(`[STORE] Fetching notes for deal: ${dealId}`);
      
      // First fetch the notes without trying to join with user data
      // Rimuoviamo il limite di 20 note che veniva applicato automaticamente da Supabase
      const { data, error } = await supabase
        .from('deal_notes')
        .select('*, attachments:note_attachments(*)')
        .eq('deal_id', dealId)
        .order('created_at', { ascending: false })
        .limit(100); // Esplicitamente impostato un limite alto per garantire che tutte le note vengano caricate

      if (error) throw error;
      
      // If we have notes, we'll add author information by fetching user profiles separately
      if (data && data.length > 0) {
        // Fetch users directly from auth.users to get the metadata
        for (const note of data) {
          if (!note.user_id) {
            // Nota di sistema (webhook, automazione)
            note.author = {
              id: 'system',
              email: 'system@flusia.it',
              full_name: 'Flusia',
              profile_image_url: 'https://flusia.it/favicon.ico'
            };
          } else {
            try {
              // Get user profile data - note that user_id is actually stored in the id field of user_profiles
              const { data: userProfile, error: profileError } = await supabase
                .from('user_profiles')
                .select('id, full_name, email, profile_image_url, avatar_url')
                .eq('id', note.user_id)
                .maybeSingle();

              if (!profileError && userProfile) {
                note.author = {
                  id: note.user_id,
                  email: userProfile.email,
                  full_name: userProfile.full_name,
                  profile_image_url: (userProfile as any).avatar_url || userProfile.profile_image_url
                };
              } else {
                // Fallback: Get current user if it matches the note's user_id
                const { data: { user } } = await supabase.auth.getUser();
                if (user && user.id === note.user_id) {
                  note.author = {
                    id: note.user_id,
                    email: user.email,
                    full_name: user.user_metadata?.full_name || 'User',
                    profile_image_url: user.user_metadata?.profile_image_url
                  };
                } else {
                  // Default author information if we can't find the user
                  const userData = await getUserData();
                  note.author = {
                    id: note.user_id,
                    email: 'user@example.com',
                    full_name: `User (${note.user_id.substring(0, 6)})`,
                    profile_image_url: null
                  };
                }
              }
            } catch (userError) {
              console.error('Error fetching user data for note:', userError);
              // Set default author information on error
              note.author = {
                id: note.user_id,
                email: 'user@example.com',
                full_name: `User ${note.user_id.substring(0, 6)}`,
                profile_image_url: null
              };
            }
          }
        }
      }
      
      return data || [];
    } catch (error) {
      console.error('Error fetching deal notes:', error);
      return [];
    }
  },
  
  addDealNote: async (dealId: string, content: string, metadata?: any) => {
    try {
      const userData = await getUserData();
      if (!userData?.userId) throw new Error("Not authenticated");
      
      const noteData = {
        deal_id: dealId,
        user_id: userData.userId,
        content,
        created_at: new Date().toISOString(),
        metadata,
        organization_id: userData.organizationId,
        is_edited: false,
        edit_count: 0
      };
      
      // Insert note
      const { data: insertedNote, error } = await supabase
        .from('deal_notes')
        .insert(noteData)
        .select('*')
        .single();
        
      if (error) throw error;
      
      // Now get user profile data for the current user
      const { data: authorData, error: userError } = await supabase
        .from('user_profiles')
        .select('id, full_name, email, profile_image_url, avatar_url')
        .eq('id', userData.userId)  // In user_profiles, the id field actually contains the user_id
        .single();
        
      // Get the current user's data as fallback
      const { data: { user } } = await supabase.auth.getUser();
      
      // Combine the data
      const data = {
        ...insertedNote,
        author: !userError && authorData ? {
          id: authorData.id,
          email: authorData.email,
          full_name: authorData.full_name,
          profile_image_url: (authorData as any).avatar_url || authorData.profile_image_url
        } : user ? {
          id: user.id,
          email: user.email,
          full_name: user.user_metadata?.full_name || 'User',
          profile_image_url: user.user_metadata?.profile_image_url
        } : {
          id: userData.userId,
          email: 'user@example.com',
          full_name: `User (${userData.userId.substring(0, 6)})`,
          profile_image_url: null
        }
      };
        
      if (error) throw error;
      
      // Update the current deal if it's loaded
      const currentDeals = get().deals;
      const dealIndex = currentDeals.findIndex(d => d.id === dealId);

      if (dealIndex >= 0) {
        const updatedDeal = {
          ...currentDeals[dealIndex],
          notes: [
            ...(currentDeals[dealIndex].notes || []),
            data
          ]
        };

        const updatedDeals = [...currentDeals];
        updatedDeals[dealIndex] = updatedDeal;

        set({ deals: updatedDeals });
      }

      // Emit event for workflow triggers
      try {
        // Get deal title for the event
        const deal = currentDeals.find(d => d.id === dealId);
        console.log('🔄 [DEAL STORE] Emitting deal:note_created event');
        eventBus.emitEvent({
          type: 'deal:note_created',
          payload: {
            dealId,
            noteId: insertedNote.id,
            noteContent: content,
            title: deal?.title
          }
        });
      } catch (eventError) {
        console.error('Error emitting deal:note_created event:', eventError);
        // Don't fail note creation if event fails
      }

      return data;
    } catch (error) {
      console.error('Error adding deal note:', error);
      return null;
    }
  },

  updateDealNote: async (noteId: string, content: string) => {
    try {
      const userData = await getUserData();
      if (!userData?.userId) throw new Error("Not authenticated");

      // First fetch the current note to create a version from it
      const { data: currentNote, error: fetchError } = await supabase
        .from('deal_notes')
        .select('*')
        .eq('id', noteId)
        .single();

      if (fetchError) throw fetchError;
      if (!currentNote) throw new Error("Note not found");
      
      // Create a version entry for the current version before updating
      const versionData = {
        note_id: noteId,
        content: currentNote.content,
        user_id: currentNote.user_id,
        created_at: new Date().toISOString()
      };

      // Save version history
      const { error: versionError } = await supabase
        .from('deal_note_versions')
        .insert(versionData);

      if (versionError) {
        console.error('Error saving note version:', versionError);
        // Continue despite version error - this is not critical
      }

      // Update the note with new content
      const updatedData = {
        content,
        updated_at: new Date().toISOString(),
        is_edited: true,
        edit_count: (currentNote.edit_count || 0) + 1
      };

      const { data: updatedNote, error: updateError } = await supabase
        .from('deal_notes')
        .update(updatedData)
        .eq('id', noteId)
        .select()
        .single();

      if (updateError) throw updateError;

      // Get author info for the updated note
      const { data: authorData } = await supabase
        .from('user_profiles')
        .select('id, full_name, email, profile_image_url, avatar_url')
        .eq('id', currentNote.user_id || userData.userId)
        .single();

      // Combine the data with author info
      const enrichedNote = {
        ...updatedNote,
        author: authorData ? {
          id: authorData.id,
          email: authorData.email,
          full_name: authorData.full_name,
          profile_image_url: (authorData as any).avatar_url || authorData.profile_image_url
        } : undefined
      };

      return enrichedNote;
    } catch (error) {
      console.error('Error updating deal note:', error);
      throw error;
    }
  },

  deleteDealNote: async (noteId: string, dealId: string) => {
    try {
      console.log(`[STORE] 🗑️ Attempting to delete note: ${noteId} from deal: ${dealId}`);
      
      // Delete the note from the database
      const { error } = await supabase
        .from('deal_notes')
        .delete()
        .eq('id', noteId);
        
      if (error) throw error;
      
      // Update local state if successful
      const currentDeals = get().deals;
      const dealIndex = currentDeals.findIndex(d => d.id === dealId);
      
      if (dealIndex >= 0 && currentDeals[dealIndex].notes) {
        const updatedDeals = [...currentDeals];
        updatedDeals[dealIndex] = {
          ...updatedDeals[dealIndex],
          notes: updatedDeals[dealIndex].notes?.filter(note => note.id !== noteId) || []
        };
        
        set({ deals: updatedDeals });
      }
      
      console.log(`[STORE] ✅ Successfully deleted note: ${noteId}`);
      return true;
    } catch (error) {
      console.error('[STORE] ❌ Error deleting note:', error);
      return false;
    }
  },
  
  deleteDealActivity: async (activityId: string, dealId: string) => {
    try {
      console.log(`[STORE] 🗑️ Attempting to delete activity: ${activityId} from deal: ${dealId}`);

      // Bridge delete evento Google collegato: senza questo l'evento resta orfano
      // su Google (stesso pattern di calendarStore.deleteEvent).
      const { data: activityRow } = await supabase
        .from('sales_activities')
        .select('google_event_id, calendar_id')
        .eq('id', activityId)
        .maybeSingle();

      // Delete the activity from the database
      const { error } = await supabase
        .from('sales_activities')
        .delete()
        .eq('id', activityId);

      if (error) throw error;

      if (activityRow?.google_event_id && activityRow?.calendar_id) {
        try {
          await useCalendarStore.getState().deleteGoogleEvent(activityRow.google_event_id, activityRow.calendar_id);
        } catch (gErr) {
          console.warn('[STORE] Google delete bridge failed:', gErr);
        }
      }

      // Update local state if successful
      const currentDeals = get().deals;
      const dealIndex = currentDeals.findIndex(d => d.id === dealId);
      
      if (dealIndex >= 0 && currentDeals[dealIndex].activities) {
        const updatedDeals = [...currentDeals];
        updatedDeals[dealIndex] = {
          ...updatedDeals[dealIndex],
          activities: updatedDeals[dealIndex].activities?.filter(activity => activity.id !== activityId) || []
        };
        
        set({ deals: updatedDeals });
      }
      
      console.log(`[STORE] ✅ Successfully deleted activity: ${activityId}`);
      return true;
    } catch (error) {
      console.error('[STORE] ❌ Error deleting activity:', error);
      return false;
    }
  },

  fetchNoteVersions: async (noteId: string) => {
    try {
      // Fetch all versions for the given note
      const { data: versions, error } = await supabase
        .from('deal_note_versions')
        .select('*')
        .eq('note_id', noteId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Fetch user data for each version
      const enrichedVersions = await Promise.all((versions || []).map(async (version) => {
        if (!version.user_id) return version;
        
        const { data: userData } = await supabase
          .from('user_profiles')
          .select('id, full_name, email, profile_image_url, avatar_url')
          .eq('id', version.user_id)
          .single();
          
        return {
          ...version,
          author: userData ? {
            id: userData.id,
            email: userData.email,
            full_name: userData.full_name,
            profile_image_url: (userData as any).avatar_url || userData.profile_image_url
          } : undefined
        };
      }));

      return enrichedVersions;
    } catch (error) {
      console.error('Error fetching note versions:', error);
      return [];
    }
  },

  fetchDeals: async () => {
    // Previeni fetch paralleli
    if (get().loading) {
      console.log('[DealStore] Fetch already in progress, skipping...');
      return;
    }

    set({ loading: true, error: null });
    try {
      // Get current user's organization ID
      const userData = await getUserData();
      if (!userData?.userId) throw new Error("Not authenticated");
      if (!userData?.organizationId) throw new Error("User not associated with an organization");

      // Get the selectedUserId from the store
      const { selectedUserId } = get();

      // Query della LISTA — performance: esclusi i campi descrizione pesanti
      // (deal_services.description/invoice_description, service.description/
      // short_description: ~4.6MB su 7MB di payload per org grandi).
      // Dettaglio ed edit usano fetchDealById che carica il deal completo.
      let query = supabase
        .from('deals')
        .select(`
          *,
          client:clients (
            id,
            business_name,
            vat_number,
            sales_rep_id,
            phone,
            whatsapp_number,
            street_address,
            street_number,
            postal_code,
            city,
            province
          ),
          assignee:user_profiles!deals_assigned_to_fkey (
            full_name,
            email,
            avatar_url,
            profile_image_url
          ),
          deal_services (
            id,
            service_id,
            payment_method_id,
            amount,
            discounted_amount,
            payment_schedule,
            monthly_duration,
            custom_service_name,
            metadata,
            contract_expiry_date,
            recurring_status,
            renewal_status,
            requires_manual_renewal,
            renewal_notice_days,
            renewal_reminder_date,
            renewal_task_created,
            next_renewal_date,
            suspended_months,
            service:service_id (
              id,
              title,
              service_type,
              category_id
            ),
            payment_method:payment_method_id (
              id,
              name,
              payment_schedule
            ),
            deal_variables (
              id,
              name,
              amount,
              type,
              base_amount,
              is_cost,
              is_vat_exempt,
              include_in_pdf,
              is_confirmed,
              sort_order
            )
          ),
          notes:deal_notes (
            id,
            content,
            created_at,
            user_id,
            is_edited,
            edit_count,
            updated_at,
            metadata,
            note_type
          ),
          activities:sales_activities!sales_activities_deal_id_fkey (
            id,
            title,
            type,
            status,
            due_date
          )
        `)
        .eq('organization_id', userData.organizationId);

      // Determine if we need client-side filtering for sales_rep_id
      // Supabase doesn't support .or() on related tables, so we need to handle this client-side
      let needsClientSideFilter = false;
      let assignedToUsers: string[] = [];
      let salesRepUsers: string[] = [];
      let bothUsers: string[] = [];

      // "Non assegnata" non e' l'id di una persona: chiede le offerte con la
      // casella vuota. Trattandola come un id il confronto non tornava mai e
      // l'elenco restava vuoto (stesso difetto visto sui lead il 09/09/2026).
      let cercaNonAssegnate = false;
      if (selectedUserId && Array.isArray(selectedUserId) && selectedUserId.length > 0) {
        selectedUserId.forEach(uf => {
          if (uf.userId === '__unassigned__') {
            cercaNonAssegnate = true;
            needsClientSideFilter = true;
            return;
          }
          if (uf.mode === 'assigned_to') {
            assignedToUsers.push(uf.userId);
          } else if (uf.mode === 'sales_rep_id') {
            salesRepUsers.push(uf.userId);
            needsClientSideFilter = true; // Can't filter this server-side
          } else { // 'both'
            bothUsers.push(uf.userId);
            needsClientSideFilter = true; // Can't filter this server-side for sales_rep
          }
        });

        // If we only have assigned_to mode, we can do server-side filtering
        if (!needsClientSideFilter && assignedToUsers.length > 0) {
          query = query.in('assigned_to', assignedToUsers);
        }
        // Otherwise, we'll filter client-side after fetching
      }

      // Execute the query with ordering
      let { data, error } = await query.order('created_at', { ascending: false });

      // Apply client-side filtering if needed (for sales_rep_id modes)
      if (data && needsClientSideFilter && selectedUserId && Array.isArray(selectedUserId) && selectedUserId.length > 0) {
        data = data.filter(deal => {
          // Check if deal matches any of the selected users based on their modes
          if (cercaNonAssegnate && !deal.assigned_to && !deal.client?.sales_rep_id) return true;

          const matchesAssignedTo =
            assignedToUsers.includes(deal.assigned_to) ||
            bothUsers.includes(deal.assigned_to);

          const clientSalesRepId = deal.client?.sales_rep_id;
          const matchesSalesRep = clientSalesRepId && (
            salesRepUsers.includes(clientSalesRepId) ||
            bothUsers.includes(clientSalesRepId)
          );

          return matchesAssignedTo || matchesSalesRep;
        });
      }

      if (error) throw error;
      set({ deals: data || [] });
    } catch (error) {
      set({ error: friendlyDbError(error) });
    } finally {
      set({ loading: false });
    }
  },

  searchDealsGlobal: async (query: string, limit = 50): Promise<GlobalDealSearchResult[]> => {
    const trimmed = query.trim();
    if (trimmed.length < 2) return [];
    try {
      const userData = await getUserData();
      if (!userData?.organizationId) return [];

      // Step 1: cerca per titolo/descrizione deal (server-side ilike)
      const escaped = trimmed.replace(/[%,()]/g, ' ').trim();
      const orFilter = `title.ilike.%${escaped}%,description.ilike.%${escaped}%`;
      const { data: byDeal, error: dealErr } = await supabase
        .from('deals')
        .select('id, title, status, created_at, amount, client_id, client:clients(business_name)')
        .eq('organization_id', userData.organizationId)
        .or(orFilter)
        .order('created_at', { ascending: false })
        .limit(limit);
      if (dealErr) {
        console.warn('[searchDealsGlobal] deal title/desc search error:', dealErr.message);
      }

      // Step 2: cerca per business_name cliente, poi prendi i deal di quei clienti
      const { data: matchingClients, error: clientErr } = await supabase
        .from('clients')
        .select('id, business_name')
        .eq('organization_id', userData.organizationId)
        .ilike('business_name', `%${escaped}%`)
        .limit(limit);
      if (clientErr) {
        console.warn('[searchDealsGlobal] client name search error:', clientErr.message);
      }

      let byClient: any[] = [];
      if (matchingClients && matchingClients.length > 0) {
        const clientIds = matchingClients.map(c => c.id);
        const { data: dealsByClient } = await supabase
          .from('deals')
          .select('id, title, status, created_at, amount, client_id, client:clients(business_name)')
          .eq('organization_id', userData.organizationId)
          .in('client_id', clientIds)
          .order('created_at', { ascending: false })
          .limit(limit);
        byClient = dealsByClient || [];
      }

      // Merge dedup per id
      const map = new Map<string, GlobalDealSearchResult>();
      for (const d of [...(byDeal || []), ...byClient]) {
        if (!d || !d.id || map.has(d.id)) continue;
        map.set(d.id, {
          id: d.id,
          title: d.title ?? null,
          status: d.status ?? null,
          created_at: d.created_at ?? null,
          amount: typeof d.amount === 'number' ? d.amount : null,
          client_id: d.client_id ?? null,
          client_business_name: (d.client as any)?.business_name ?? null,
        });
      }
      return Array.from(map.values()).slice(0, limit);
    } catch (err) {
      console.error('[searchDealsGlobal] unexpected error:', err);
      return [];
    }
  },

  fetchAllDeals: async () => {
    set({ loading: true, error: null });
    try {
      // Get current user's organization ID
      const userData = await getUserData();
      if (!userData?.userId) throw new Error("Not authenticated");
      if (!userData?.organizationId) throw new Error("User not associated with an organization");

      // Get the selectedUserId from the store
      const { selectedUserId } = get();
      
      // Create query with base selection and organization filter
      let query = supabase
        .from('deals')
        .select(`
          *,
          client:clients (
            id,
            business_name,
            sales_rep_id,
            phone,
            whatsapp_number,
            street_address,
            street_number,
            postal_code,
            city,
            province
          ),
          deal_services (
            id,
            service_id,
            amount,
            discounted_amount,
            payment_method_id,
            payment_schedule,
            monthly_duration,
            custom_service_name,
            service:service_id (
              id,
              title
            )
          ),
          notes:deal_notes (
            id,
            content,
            created_at,
            user_id,
            is_edited,
            edit_count,
            updated_at,
            metadata,
            note_type
          )
        `)
        .eq('organization_id', userData.organizationId);

      // Determine if we need client-side filtering for sales_rep_id
      let needsClientSideFilter = false;
      let assignedToUsers: string[] = [];
      let salesRepUsers: string[] = [];
      let bothUsers: string[] = [];

      // "Non assegnata" non e' l'id di una persona: chiede le offerte con la
      // casella vuota. Trattandola come un id il confronto non tornava mai e
      // l'elenco restava vuoto (stesso difetto visto sui lead il 09/09/2026).
      let cercaNonAssegnate = false;
      if (selectedUserId && Array.isArray(selectedUserId) && selectedUserId.length > 0) {
        selectedUserId.forEach(uf => {
          if (uf.userId === '__unassigned__') {
            cercaNonAssegnate = true;
            needsClientSideFilter = true;
            return;
          }
          if (uf.mode === 'assigned_to') {
            assignedToUsers.push(uf.userId);
          } else if (uf.mode === 'sales_rep_id') {
            salesRepUsers.push(uf.userId);
            needsClientSideFilter = true;
          } else { // 'both'
            bothUsers.push(uf.userId);
            needsClientSideFilter = true;
          }
        });

        // If we only have assigned_to mode, we can do server-side filtering
        if (!needsClientSideFilter && assignedToUsers.length > 0) {
          query = query.in('assigned_to', assignedToUsers);
        }
      }

      // Execute the query with ordering
      let { data, error } = await query.order('created_at', { ascending: false });

      // Apply client-side filtering if needed (for sales_rep_id modes)
      if (data && needsClientSideFilter && selectedUserId && Array.isArray(selectedUserId) && selectedUserId.length > 0) {
        data = data.filter(deal => {
          if (cercaNonAssegnate && !deal.assigned_to && !deal.client?.sales_rep_id) return true;

          const matchesAssignedTo =
            assignedToUsers.includes(deal.assigned_to) ||
            bothUsers.includes(deal.assigned_to);

          const clientSalesRepId = deal.client?.sales_rep_id;
          const matchesSalesRep = clientSalesRepId && (
            salesRepUsers.includes(clientSalesRepId) ||
            bothUsers.includes(clientSalesRepId)
          );

          return matchesAssignedTo || matchesSalesRep;
        });
      }

      if (error) throw error;
      set({ deals: data || [] });
    } catch (error) {
      set({ error: friendlyDbError(error) });
    } finally {
      set({ loading: false });
    }
  },

  // Simplified tracking for all fetch operations
  _activeFetchRequests: {} as Record<string, { startTime: number, lastSuccess: number, inProgress: boolean }>,

  fetchDealById: async (id: string, force = false) => {
    const store = get();
    const fetchId = `deal-${id}`;
    const now = Date.now();
    const requestInfo = store._activeFetchRequests[fetchId] || { startTime: 0, lastSuccess: 0, inProgress: false };

    // Check if there's already an active fetch for this deal.
    // `force` salta la dedup: dopo un salvataggio il chiamante vuole il deal
    // fresco e completo — il fallback sotto restituisce il deal in formato
    // LISTA (campi ridotti), che in testata dettaglio appare stale/incompleto.
    if (!force && requestInfo.inProgress) {
      const elapsed = now - requestInfo.startTime;

      // If the fetch has been running less than 2 seconds, use cached data if available
      if (elapsed < 2000) {
        const existingDeal = get().deals.find(d => d.id === id);
        if (existingDeal) {
          console.log(`[STORE] Using cached deal data for ${id} (fetch in progress for ${elapsed}ms)`);
          return existingDeal;
        }
      }
    }

    // Mark this fetch as in progress
    set({
      _activeFetchRequests: {
        ...store._activeFetchRequests,
        [fetchId]: {
          startTime: now,
          lastSuccess: requestInfo.lastSuccess,
          inProgress: true
        }
      }
    });

    // For performance tracking
    const startTime = performance.now();
    
    try {
      // First get the deal data with services and variables
      const { data, error } = await supabase
        .from('deals')
        .select(`
          *,
          client:clients (
            business_name,
            phone,
            email,
            whatsapp_number,
            contacts:client_contacts!client_contacts_client_id_fkey (
              id,
              name,
              phone,
              email,
              role,
              area,
              is_primary
            )
          ),
          assignee:user_profiles!deals_assigned_to_fkey (
            full_name,
            email,
            avatar_url,
            profile_image_url
          ),
          payment_method:payment_methods (
            id,
            name
          ),
          deal_services (
            id,
            service_id,
            payment_method_id,
            amount,
            discounted_amount,
            description,
            invoice_description,
            payment_schedule,
            monthly_duration,
            custom_service_name,
            metadata,
            vat_rate,
            line_type,
            product_id,
            quantity,
            unit_of_measure,
            unit_price,
            contract_expiry_date,
            recurring_status,
            renewal_status,
            requires_manual_renewal,
            renewal_notice_days,
            renewal_reminder_date,
            renewal_task_created,
            next_renewal_date,
            suspended_months,
            service:service_id (
              id,
              title,
              description,
              short_description,
              service_type,
              has_renewal_tasks,
              renewal_task_intervals
            ),
            payment_method:payment_method_id (
              id,
              name,
              payment_schedule
            ),
            deal_variables (
              id,
              name,
              description,
              amount,
              type,
              base_amount,
              is_cost,
              is_vat_exempt,
              include_in_pdf,
              is_confirmed,
              sort_order,
              created_at,
              updated_at
            ),
            addons:deal_service_addons (
              id,
              service_addon_id,
              name,
              description,
              amount,
              is_required,
              is_included_in_base
            )
          ),
          deal_contacts (
            id,
            contact_id,
            person_client_id,
            created_at,
            contact:client_contacts (
              id,
              name,
              phone,
              email,
              role,
              area,
              is_primary
            )
          )
        `)
        .eq('id', id)
        .single();

      if (error) {
        throw error;
      }
      
      if (!data) {
        throw new Error(`Deal with ID ${id} not found in database`);
      }

      // Risolve le persone dei deal_contacts (person_client_id NON è FK → no embed PostgREST).
      // Batch query separata, poi attach su ogni deal_contact.
      const personIds = ((data as any).deal_contacts || [])
        .map((dc: any) => dc.person_client_id)
        .filter(Boolean);
      if (personIds.length > 0) {
        const { data: persons } = await supabase
          .from('clients')
          .select('id, business_name, email, phone')
          .in('id', personIds);
        const personMap = new Map((persons || []).map((p: any) => [p.id, p]));
        for (const dc of (data as any).deal_contacts) {
          if (dc.person_client_id) dc.person = personMap.get(dc.person_client_id) || null;
        }
      }

      // Modello Contatti/Aziende first-class: la lista "tutti i contatti dell'azienda"
      // (client.contacts) va letta anche da client_relationships (client_contacts è vuota
      // per i clienti nuovi), altrimenti il selettore destinatari proposta/WhatsApp è vuoto.
      const companyId = (data as any).client_id as string | undefined;
      if (companyId && (data as any).client) {
        const { fetchReferentiAsContacts, mergeContactsByEmail } = await import('../utils/fetchReferentiContacts');
        const refMap = await fetchReferentiAsContacts([companyId]);
        (data as any).client.contacts = mergeContactsByEmail(
          ((data as any).client.contacts ?? []) as any,
          refMap[companyId] ?? [],
        );
      }

      // Mark this fetch as completed successfully
      set({
        _activeFetchRequests: {
          ...get()._activeFetchRequests,
          [fetchId]: {
            startTime: requestInfo.startTime,
            lastSuccess: Date.now(),
            inProgress: false
          }
        }
      });

      const elapsed = performance.now() - startTime;
      console.log(`[STORE] ⏱️ fetchDealById completed in ${elapsed.toFixed(2)}ms`);

      // Return the basic deal data immediately so the component can render
      // We'll fetch notes and activities asynchronously
      return data;
    } catch (error) {
      console.error(`[STORE] ❌ Error fetching deal:`, error);

      // Mark this fetch as completed even though it errored
      set({
        _activeFetchRequests: {
          ...get()._activeFetchRequests,
          [fetchId]: {
            startTime: requestInfo.startTime,
            lastSuccess: requestInfo.lastSuccess,
            inProgress: false
          }
        },
        error: friendlyDbError(error)
      });

      return null;
    }
  },

  // Fetch activities related to a deal with simplified cache and request tracking
  fetchDealActivities: async (dealId: string, forceRefresh = false) => {
    if (!dealId) {
      console.log('[STORE] No deal ID provided for activities fetch');
      return [];
    }

    const store = get();
    const fetchId = `activities-${dealId}`;
    const now = Date.now();
    const requestInfo = store._activeFetchRequests[fetchId] || { startTime: 0, lastSuccess: 0, inProgress: false };
    const startTime = performance.now();

    // If we're forcing a refresh, bypass all cache checks
    if (!forceRefresh) {
      // Check if we already have a request in progress
      if (requestInfo.inProgress) {
        const elapsed = now - requestInfo.startTime;

        // Only allow overlapping requests if the current one is taking too long (2 seconds)
        if (elapsed < 2000) {
          console.log(`[STORE] Skipping duplicate activities fetch - already in progress (${elapsed}ms elapsed)`);
          return get().deals.find(d => d.id === dealId)?.activities || [];
        }

        console.log(`[STORE] Existing activities fetch taking too long (${elapsed}ms), allowing new fetch`);
      }

      // Rate limiting - only fetch again if it's been at least 1 second since last successful fetch
      // This prevents flooding the API with requests
      const timeSinceLastSuccess = now - requestInfo.lastSuccess;
      if (requestInfo.lastSuccess > 0 && timeSinceLastSuccess < 1000) {
        console.log(`[STORE] Rate limiting activities fetch (${timeSinceLastSuccess}ms since last fetch)`);
        return get().deals.find(d => d.id === dealId)?.activities || [];
      }
    } else {
      console.log(`[STORE] Forcing refresh of activities for deal ${dealId}`);
    }

    // Update fetch status - this single update replaces multiple tracking variables
    set({
      _activeFetchRequests: {
        ...store._activeFetchRequests,
        [fetchId]: {
          startTime: now,
          lastSuccess: requestInfo.lastSuccess,
          inProgress: true
        }
      }
    });

    try {
      console.log(`[STORE] 📡 Fetching activities for deal ${dealId}`);

      // First check if the table exists with a lightweight query
      const { error: checkError } = await supabase
        .from('sales_activities')
        .select('id', { count: 'exact', head: true })
        .limit(1);

      // If table doesn't exist yet, return empty array
      if (checkError) {
        console.log(`[STORE] Sales activities table not found or unavailable`);
        return [];
      }

      // Check if the user_id column exists for proper join strategy
      const { data: columnCheck, error: columnError } = await supabase
        .from('sales_activities')
        .select('user_id')
        .limit(1);

      const hasUserProfileId = !columnError && columnCheck && columnCheck.length > 0 &&
                              'user_id' in columnCheck[0];

      // Build the appropriate query based on schema availability
      const query = supabase
        .from('sales_activities')
        .select(hasUserProfileId ? `
          id,
          title,
          description,
          type,
          status,
          due_date,
          user_id,
          assigned_to,
          created_at,
          completed_at,
          outcome,
          users:user_profiles!sales_activities_user_id_fkey1(
            id,
            email,
            full_name,
            profile_image_url
          ),
          assignee:user_profiles!sales_activities_assigned_to_fkey(
            id,
            email,
            full_name,
            profile_image_url
          )
        ` : `
          id,
          title,
          description,
          type,
          status,
          due_date,
          user_id,
          assigned_to,
          created_at,
          completed_at,
          outcome
        `)
        .eq('deal_id', dealId)
        .order('created_at', { ascending: false });

      // Execute the query
      const { data, error } = await query;

      if (error) {
        console.error('[STORE] ❌ Error fetching activities data:', error);
        return [];
      }

      const activitiesWithAssignee = data || [];
      console.log(`[STORE] ✅ Found ${activitiesWithAssignee.length} activities for deal ${dealId}`);
      console.log(`[STORE] ✅ Loaded ${activitiesWithAssignee.filter(a => a.assignee).length} activities with assignee data`);

      // Update deal state with the new activities if needed
      const currentDeals = get().deals;
      const dealIndex = currentDeals.findIndex(d => d.id === dealId);

      if (dealIndex >= 0) {
        // Only update state if activities have changed to avoid unnecessary renders
        const currentActivities = currentDeals[dealIndex].activities || [];
        const activityIds = new Set(activitiesWithAssignee.map(a => a.id));
        const currentActivityIds = new Set(currentActivities.map(a => a.id));

        // Check if sets are different by comparing sizes and individual items
        const hasChanges =
          activityIds.size !== currentActivityIds.size ||
          activitiesWithAssignee.some(a => !currentActivityIds.has(a.id)) ||
          currentActivities.some(a => !activityIds.has(a.id));

        if (hasChanges) {
          const updatedDeals = [...currentDeals];
          updatedDeals[dealIndex] = {
            ...updatedDeals[dealIndex],
            activities: activitiesWithAssignee
          };

          console.log(`[STORE] Updating deal state with ${activitiesWithAssignee.length} activities`);
          set({ deals: updatedDeals });
        } else {
          console.log(`[STORE] Activities unchanged, skipping state update`);
        }
      }

      // Update request tracking with success
      set({
        _activeFetchRequests: {
          ...get()._activeFetchRequests,
          [fetchId]: {
            startTime: requestInfo.startTime,
            lastSuccess: now,
            inProgress: false
          }
        }
      });

      const elapsed = performance.now() - startTime;
      console.log(`[STORE] ⏱️ Activities fetch completed in ${elapsed.toFixed(2)}ms`);

      return activitiesWithAssignee;
    } catch (error) {
      console.error('[STORE] ⚠️ Error fetching activities:', error);

      // Mark request as complete even on error
      set({
        _activeFetchRequests: {
          ...get()._activeFetchRequests,
          [fetchId]: {
            startTime: requestInfo.startTime,
            lastSuccess: requestInfo.lastSuccess,
            inProgress: false
          }
        }
      });

      return [];
    }
  },

  createDeal: async (deal: NewDeal, services: DealServiceFormData[] = [], contactIds?: string[], opts?: { skipWorkflows?: boolean }) => {
    const flowId = `flow-${Date.now()}`;
    console.log(`🚀 [${flowId}] createDeal called with:`, { deal, services, contactIds });
    console.log(`🔍 [${flowId}] Services count:`, services.length);
    
    // Log variables in each service
    services.forEach((service, index) => {
      console.log(`📋 [${flowId}] Service ${index + 1}:`, service.service?.name || 'unnamed');
      console.log(`🧮 [${flowId}] Service ${index + 1} variables:`, service.variables?.length || 0, service.variables);
    });
    try {
      // Calculate total amount from services, considering monthly duration
      const totalAmount = services.reduce((sum, service) => {
        const baseAmount = service.discounted_amount ?? service.amount;
        
        // If it's a monthly service with a defined duration, multiply by the number of months
        const isMonthlyService = service.service?.service_type === 'monthly';
        if (isMonthlyService && service.monthly_duration) {
          return sum + (baseAmount * service.monthly_duration);
        }
        
        // Otherwise just add the base amount
        return sum + baseAmount;
      }, 0);

      // Get the user's organization using the centralized utility
      const userData = await getUserData();
      if (!userData?.userId) throw new Error("Not authenticated");
      if (!userData?.organizationId) throw new Error("User not associated with an organization");

      // Get settings to check for quote number configuration
      const { data: settingsData, error: settingsError } = await supabase
        .from('settings')
        .select('last_quote_number, last_quote_year, quote_number_start')
        .eq('organization_id', userData.organizationId)
        .order('updated_at', { ascending: false })
        .limit(1);

      if (settingsError) {
        console.error('Error fetching settings:', settingsError);
      }

      // Get the latest settings
      const settings = settingsData && settingsData.length > 0 ? settingsData[0] : null;

      // Generate quote number if not specified
      let quoteNumber = deal.quote_number;
      console.log(`🔍 Starting quote number generation with initial value: ${quoteNumber}`);
      
      // Use the centralized function in pdf.ts to generate the quote number
      // Only if not already specified in the deal
      if (!quoteNumber) {
        try {
          // Import the generateQuoteNumber function
          // Using a proper import with destructuring
          const pdf = await import('../pdf');
          if (!pdf.generateQuoteNumber) {
            throw new Error('generateQuoteNumber function not found in pdf module');
          }
          
          // Create a temporary object with the correct format for the function
          const tempDeal = {
            ...deal,
            organization_id: userData.organizationId
          };
          
          // Pass settings if available
          const settingsObj = settings || {
            last_quote_number: 0,
            last_quote_year: new Date().getFullYear(),
            quote_number_start: 1
          };
          
          // IMPORTANT: This will generate a new number and automatically increment
          // the counter in the organization settings
          console.log('🔄 Generating new quote number with atomic RPC function...');
          quoteNumber = await pdf.generateQuoteNumber(tempDeal, settingsObj);
          console.log(`✅ Successfully generated new quote number: ${quoteNumber}`);
        } catch (err) {
          // Fallback in case generation fails
          console.error('❌ Error generating quote number:', err);
          
          // Make sure we have a properly formatted fallback
          const currentYear = new Date().getFullYear();
          const fallbackNumber = '001'; // Ensure it has 3 digits with padding
          quoteNumber = `${fallbackNumber}/${currentYear}`;
          console.log(`⚠️ Using fallback quote number: ${quoteNumber}`);
          
          // In case of failure, try to create/update settings in the traditional way
          try {
            if (settings) {
              // Traditional increment
              const nextQuoteNumber = (settings.last_quote_number || 0) + 1;
              const { error: updateError } = await supabase
                .from('settings')
                .update({
                  last_quote_number: nextQuoteNumber,
                  last_quote_year: currentYear
                })
                .eq('organization_id', userData.organizationId);
                
              if (!updateError) {
                console.log(`✅ Quote number counter updated to ${nextQuoteNumber} (fallback method)`);
              }
            } else {
              // Create settings if missing
              await supabase
                .from('settings')
                .insert({
                  organization_id: userData.organizationId,
                  last_quote_number: 1,
                  last_quote_year: currentYear,
                  quote_number_start: 1
                });
              console.log('✅ Created settings with initial quote number (fallback)');
            }
          } catch (settingsErr) {
            console.error('❌ Error updating settings (fallback):', settingsErr);
          }
        }
      }

      // Verifica se il primo servizio è un servizio personalizzato o una riga prodotto magazzino
      const firstService = services[0];
      const isFirstServiceCustom = firstService && firstService.service_id?.toString().startsWith('custom-');
      const isFirstServiceProduct = firstService && (firstService as any).line_type === 'product';
      
      // Double-check we have a valid quote number before creating the deal
      console.log(`🔍 Final quote number before creating deal: ${quoteNumber}`);
      if (!quoteNumber) {
        // Last resort fallback if somehow we still don't have a quote number
        const currentYear = new Date().getFullYear();
        quoteNumber = `001/${currentYear}`;
        console.log(`⚠️ Using emergency fallback quote number: ${quoteNumber}`);
      }
      
      // IMPORTANT: Ensure the quote number is properly formatted with 3 digits
      // This fixes the issue where quote numbers are saved as "01/2025" instead of "001/2025"
      if (quoteNumber && typeof quoteNumber === 'string') {
        const parts = quoteNumber.split('/');
        if (parts.length === 2) {
          const [number, year] = parts;
          // Re-format with 3-digit padding to ensure consistent format
          const formattedNumber = number.toString().padStart(3, '0');
          quoteNumber = `${formattedNumber}/${year}`;
          console.log(`🔄 Re-formatted quote number to ensure 3-digit padding: ${quoteNumber}`);
        }
      }

      // Create the deal.
      // skipWorkflows: invia l'header x-no-workflow=true SOLO su questo insert, così il trigger DB
      // trg_deals_workflow salta la notifica (creazione "silenziosa" es. dalla griglia previsionale).
      // NB: supabase.rest.headers è un oggetto Headers → usare set()/delete() (non bracket).
      const setNoWorkflowHeader = (on: boolean) => {
        if (!opts?.skipWorkflows) return;
        try {
          const h: any = (supabase as any).rest?.headers;
          if (!h) return;
          if (typeof h.set === 'function') { on ? h.set('x-no-workflow', 'true') : h.delete('x-no-workflow'); }
          else { on ? (h['x-no-workflow'] = 'true') : (delete h['x-no-workflow']); }
        } catch { /* noop */ }
      };
      setNoWorkflowHeader(true);
      let dealData: any, dealError: any;
      try {
        ({ data: dealData, error: dealError } = await supabase
          .from('deals')
          .insert({
            ...deal,
            quote_number: quoteNumber,
            amount: totalAmount,
            // If the first service is custom, set service_id to null
            service_id: (isFirstServiceCustom || isFirstServiceProduct) ? null : services[0]?.service_id,
            // Se l'offerta ha un metodo suo (lo si sceglie quando contiene
            // prodotti) vince quello. Altrimenti resta la vecchia copia del
            // metodo della prima riga: le offerte di soli servizi non cambiano
            // di una virgola.
            payment_method_id: (deal as any).payment_method_id || services[0]?.payment_method_id,
            organization_id: userData.organizationId
          })
          .select()
          .single());
      } finally {
        setNoWorkflowHeader(false);
      }
        
      // Log the created deal data for debugging
      if (dealData) {
        console.log(`✅ Deal created with ID: ${dealData.id}, quote_number: ${dealData.quote_number}`);
      }

      if (dealError) throw dealError;

      dispatchTaskCompleted('deals', 'deal_store_create');

      // FIX 2026-06-06: incrementa settings.last_quote_number quando il quote_number
      // è in formato "NNN/YYYY" (preimpostato dal hook useNextQuoteNumber sul form).
      // Senza questo incremento il prossimo deal mostra lo stesso numero come preview
      // e si generano duplicati (es. tre "128/2026" su Obly).
      // NOTA: il path che usa generateQuoteNumber() in pdf.ts (formato Q-YYYY-NNN)
      // ha la propria logica e non passa di qui.
      if (dealData?.quote_number) {
        const numericMatch = /^(\d+)\/(\d{4})$/.exec(dealData.quote_number);
        if (numericMatch) {
          const usedSeq = parseInt(numericMatch[1], 10);
          const usedYear = parseInt(numericMatch[2], 10);
          // Dal 02/08/2026 si passa dalla RPC bump_quote_number invece che da una UPDATE
          // diretta: la RLS su settings richiede il permesso settings:update, che governa
          // l'amministrazione delle impostazioni ed e' un diritto diverso dal creare
          // offerte. Con la UPDATE diretta chi non aveva quel permesso non incrementava il
          // contatore e la prossima offerta riproponeva lo stesso numero.
          // NB: la UPDATE filtrata dalla RLS NON solleva e NON entra nel catch — torna
          // semplicemente 0 righe. Per questo l'errore va letto esplicitamente.
          try {
            const { data: nuovoNumero, error: bumpErr } = await supabase.rpc(
              'bump_quote_number',
              {
                p_organization_id: userData.organizationId,
                p_seq: usedSeq,
                p_year: usedYear,
              },
            );
            if (bumpErr) {
              console.error(
                'Numerazione preventivi non aggiornata: la prossima offerta rischia di ripetere lo stesso numero.',
                bumpErr,
              );
            } else {
              console.log(`last_quote_number portato a ${nuovoNumero} (anno ${usedYear})`);
            }
          } catch (bumpErr) {
            console.error('Numerazione preventivi non aggiornata:', bumpErr);
          }
        }
      }

      // Create deal services
      console.log(`📋 [${flowId}] Services to create:`, services);
      for (const service of services) {
        console.log(`🔍 [${flowId}] Processing service:`, service);
        console.log(`🔍 [${flowId}] Service has variables:`, service.variables?.length || 0, service.variables);
        // Verifica se è un servizio personalizzato (il cui ID inizia con "custom-")
        const isCustomService = service.service_id?.toString().startsWith('custom-');
        
        // Prepara i dati da inserire
        let description = service.description;
        
        // Se è un servizio personalizzato e non c'è ancora una descrizione, creiamone una con l'ID originale
        if (isCustomService && !description) {
          description = `Servizio personalizzato (ID: ${service.service_id})`;
        }
        
        // Prepara il metadata per i servizi personalizzati
        let metadata = service.metadata || {};
        if (isCustomService) {
          // Per i servizi personalizzati, assicuriamoci che il metadata contenga il custom_service_type
          const serviceType = service.custom_service_type || service.service?.service_type || 'one_time';
          metadata = {
            ...metadata,
            custom_service_type: serviceType
          };
          console.log(`🔍 [CREATE] Servizio personalizzato - metadata preparato:`, {
            custom_service_name: service.custom_service_name,
            custom_service_type: service.custom_service_type,
            service_type_from_service: service.service?.service_type,
            final_metadata: metadata
          });
        }

        // VIVA: righe prodotto magazzino (line_type='product') hanno colonne dedicate
        // e service_id sintetico 'product-*' che NON è una FK valida → forziamo a null
        const isProductLine = (service as any).line_type === 'product';
        const serviceData = {
          deal_id: dealData.id,
          // Servizi custom e prodotti → service_id null (no FK su services)
          service_id: (isCustomService || isProductLine) ? null : service.service_id,
          payment_method_id: service.payment_method_id,
          amount: service.amount,
          discounted_amount: service.discounted_amount,
          description: description,
          invoice_description: (service as any).invoice_description ?? null,
          payment_schedule: service.payment_schedule,
          monthly_duration: service.monthly_duration,
          // Per i servizi personalizzati, assicuriamoci che il nome personalizzato sia impostato
          custom_service_name: isCustomService ? (service.custom_service_name || 'Servizio personalizzato') : service.custom_service_name,
          // Categoria della riga scritta a mano: le righe da catalogo la ereditano
          // dal servizio, queste no. Senza, il cliente resta fuori dalle liste
          // per servizio acquistato.
          category_id: isCustomService ? ((service as any).category_id ?? null) : null,
          // Aggiungi il campo metadata con custom_service_type per servizi personalizzati
          metadata: Object.keys(metadata).length > 0 ? metadata : null,
          order: services.indexOf(service),
          // VIVA: colonne specifiche righe prodotto magazzino (default 'service' nel DB se omesso)
          line_type: isProductLine ? 'product' : 'service',
          product_id: isProductLine ? ((service as any).product_id ?? null) : null,
          quantity: isProductLine ? ((service as any).quantity ?? null) : null,
          unit_of_measure: isProductLine ? ((service as any).unit_of_measure ?? null) : null,
          unit_price: isProductLine ? ((service as any).unit_price ?? null) : null,
          // IVA per-riga: null = eredita da products/services.vat_rate (fallback 22)
          vat_rate: (service as any).vat_rate ?? null,
          // NOTA 2026-06-06: variation_id/price_list_id richiedono la migration
          // 20260605_deal_services_product_columns.sql che NON è ancora applicata in
          // produzione EU. Il loro valore è già preservato dentro `metadata` (vedi
          // prepareServiceData) per backward compat. Riabilitare le 2 colonne dedicate
          // SOLO dopo aver applicato la migration.
        };
        
        console.log('📝 Creating service in DB with data:', serviceData);
        const { data: serviceRecord, error: serviceError } = await supabase
          .from('deal_services')
          .insert(serviceData)
          .select('id')
          .single();

        if (serviceError) {
          console.error('Error creating deal service:', serviceError, 'Service data:', serviceData);
          throw serviceError;
        }

        console.log(`✅ [${flowId}] Service created with ID:`, serviceRecord?.id);

        // Persisti gli add-on selezionati per questa riga (snapshot in deal_service_addons)
        if (service.addons && service.addons.length > 0 && serviceRecord?.id) {
          await persistDealServiceAddons(serviceRecord.id, userData.organizationId, service.addons);
          console.log(`📎 [${flowId}] Persisted ${service.addons.length} add-on(s) for service ${serviceRecord.id}`);
        }

        // Save variables for this service if they exist
        if (service.variables && service.variables.length > 0) {
          try {
            console.log(`💾 [${flowId}] Saving variables for service during deal creation:`, serviceRecord.id, service.variables);
            
            // Import the deal variable store
            const { useDealVariableStore } = await import('./dealVariableStore');
            const { createServiceVariable } = useDealVariableStore.getState();
            
            for (const variable of service.variables) {
              console.log(`🔍 [${flowId}] Processing variable:`, variable.name, variable.id);
              
              // Save all variables that are temporary OR don't have an existing ID in database
              const isTemporary = variable.id && (variable.id.startsWith('temp-') || variable.id.startsWith('template-'));
              const needsSaving = isTemporary || !variable.id || variable.id === '';
              
              if (needsSaving) {
                console.log(`📝 [${flowId}] Saving variable (temporary=${isTemporary}, needsSaving=${needsSaving}):`, variable.name);
                
                const variableData = {
                  name: variable.name,
                  description: variable.description || '',
                  amount: variable.amount ?? 0,
                  type: variable.type || 'fixed',
                  base_amount: variable.base_amount,
                  is_cost: variable.is_cost ?? false,
                  is_vat_exempt: variable.is_vat_exempt ?? false,
                  is_stamp_duty: variable.is_stamp_duty ?? false,
                  is_stamp_duty_art15: variable.is_stamp_duty_art15 ?? false,
                  is_internal_cost: variable.is_internal_cost ?? false,
                  include_in_pdf: variable.include_in_pdf ?? true,
                  sort_order: variable.sort_order ?? 0
                };

                const result = await createServiceVariable(serviceRecord.id, variableData);
                console.log(`✅ [${flowId}] Variable saved during deal creation:`, variable.name, result);
              } else {
                console.log(`⏩ [${flowId}] Skipping existing variable:`, variable.name, variable.id);
              }
            }
          } catch (error) {
            console.error(`❌ [${flowId}] Error saving variables during deal creation:`, error);
            // Non-blocking error - the deal and service were still created successfully
          }
        } else {
          console.log(`📭 [${flowId}] No variables to save for service:`, serviceRecord.id);
        }
      }

      // NOTE: Auto-assegnazione referrer rimossa - viene gestita da DealForm.tsx + DealsPage.tsx
      // DealForm pre-popola il campo segnalatori con il referrer del cliente
      // DealsPage.handleCreate salva i referrer selezionati dall'utente

      // Fetch the complete deal
      const completeDeal = await get().fetchDealById(dealData.id);
      if (!completeDeal) throw new Error('Failed to fetch complete deal');
      
      // Emit event for workflow triggers (saltato in modalità skipWorkflows)
      if (!opts?.skipWorkflows) {
        try {
          console.log('🔄 [DEAL STORE] Emitting deal:created event');
          eventBus.emitEvent({
            type: 'deal:created',
            payload: {
              dealId: completeDeal.id,
              clientId: completeDeal.client_id,
              leadId: completeDeal.lead_id,
              title: completeDeal.title,
              status: completeDeal.status,
              totalValue: completeDeal.total_value
            }
          });
        } catch (eventError) {
          console.error('Error emitting event:', eventError);
          // Non fallire la creazione del deal se l'evento fallisce
        }

        // Trigger WhatsApp automation for deal creation
        triggerWhatsAppAutomation({
          entityType: 'deal',
          triggerEvent: 'create',
          entityData: completeDeal
        }).catch(err => console.error('Error triggering WhatsApp automation:', err));
      }

      // Save associated contacts if provided (modello nuovo: person_client_id = id persona)
      if (contactIds && contactIds.length > 0) {
        console.log(`📇 [${flowId}] Saving ${contactIds.length} person-contacts for deal ${dealData.id}`);
        const contactRecords = contactIds.map(personId => ({
          deal_id: dealData.id,
          person_client_id: personId,
          organization_id: userData.organizationId,
          created_by: userData.userId
        }));

        const { error: contactsError } = await supabase
          .from('deal_contacts')
          .insert(contactRecords);

        if (contactsError) {
          console.error('Error saving deal contacts:', contactsError);
          // Non bloccare la creazione del deal per errore sui contatti
        } else {
          console.log(`✅ [${flowId}] Successfully saved ${contactIds.length} contacts`);
        }
      }

      const deals = get().deals;
      set({ deals: [completeDeal, ...deals] });
      return completeDeal;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  updateDeal: async (id: string, deal: Partial<Deal>, services?: DealServiceFormData[], contactIds?: string[]) => {
    try {
      // Get current deal data to detect changes
      const { data: current } = await supabase
        .from('deals')
        .select('status, title, assigned_to, client_id, organization_id, signed_at, created_at')
        .eq('id', id)
        .single();

      const oldStatus = current?.status;
      const isStatusChange = deal.status && deal.status !== oldStatus;
      
      // If organization_id is missing, get it from the user profile
      const updatedDealData = { ...deal };
      
      // Ensure the quote number is properly formatted with 3 digits
      if (updatedDealData.quote_number && typeof updatedDealData.quote_number === 'string') {
        const parts = updatedDealData.quote_number.split('/');
        if (parts.length === 2) {
          const [number, year] = parts;
          // Re-format with 3-digit padding to ensure consistent format
          const formattedNumber = number.toString().padStart(3, '0');
          updatedDealData.quote_number = `${formattedNumber}/${year}`;
          console.log(`🔄 Re-formatted quote number during update to ensure 3-digit padding: ${updatedDealData.quote_number}`);
        }
      }
      
      if (!current?.organization_id) {
        // Get the user's organization using the centralized utility
        try {
          const userData = await getUserData();
          if (userData?.organizationId) {
            updatedDealData.organization_id = userData.organizationId;
          }
        } catch (err) {
          console.error('Error getting organization ID:', err);
          // Continue without organization_id, the API call might still succeed
        }
      }
      
      // Update deal data
      const { data: updatedDeal, error: dealError } = await supabase
        .from('deals')
        .update(updatedDealData)
        .eq('id', id)
        .select()
        .single();

      if (dealError) throw dealError;

      // Recalculate renewal task dates when signed_at changes on an accepted deal
      if (deal.signed_at && current?.signed_at && deal.signed_at !== current.signed_at) {
        try {
          const updatedRenewals = await contractRenewalService.updateRenewalTaskDates(id, deal.signed_at);
          const updatedInstances = await contractRenewalService.updateServiceTaskInstanceDates(id, deal.signed_at);
          const updatedTemplateTasks = await contractRenewalService.recalculateTemplateTaskDates(id, deal.signed_at);
          const shiftedManualTasks = await contractRenewalService.shiftManualTaskDates(id, current.signed_at, deal.signed_at);
          console.log(`Recalculated ${updatedRenewals} renewal tasks, ${updatedInstances} task instances, ${updatedTemplateTasks} template tasks, ${shiftedManualTasks} manual tasks shifted for new signed_at`);
        } catch (err) {
          console.error('Error recalculating task dates:', err);
        }
      }

      // R-Feduzi (richiesta cliente 04/06/2026): quando cambia la data di
      // riferimento del deal (signed_at se accepted, altrimenti created_at per
      // draft), ricalcoliamo `fixedDate` di tutte le installments dei
      // deal_services che sono state derivate da un template (hanno
      // `daysAfterStart > 0` o `fixedDayOfMonth` valorizzato). Le rate
      // configurate manualmente come "data fissa pura" (solo fixedDate, senza
      // daysAfterStart/fixedDayOfMonth) restano invariate.
      // Il PDF preventivo legge `fixedDate` con priorità → se non ricalcolato
      // mostra le date snapshot vecchie e non collima con la realtà.
      try {
        const oldBaseDate = current?.signed_at || current?.created_at;
        const newBaseDate = deal.signed_at ?? (deal.created_at as any) ?? oldBaseDate;

        const baseDateChanged =
          (deal.signed_at !== undefined && deal.signed_at !== current?.signed_at) ||
          ((deal as any).created_at !== undefined && (deal as any).created_at !== current?.created_at);

        if (baseDateChanged && newBaseDate) {
          const { data: dealServices } = await supabase
            .from('deal_services')
            .select('id, payment_schedule')
            .eq('deal_id', id);

          const base = new Date(newBaseDate);
          let updatedCount = 0;

          for (const ds of (dealServices || [])) {
            const ps: any = ds.payment_schedule;
            if (!ps || !Array.isArray(ps?.installments)) continue;

            let anyChanged = false;
            const newInstallments = ps.installments.map((inst: any) => {
              // Calcolo solo per installments con regola relativa
              // (daysAfterStart > 0 oppure fixedDayOfMonth valorizzato).
              // Le "vista fattura" (daysAfterStart=0 senza fixedDayOfMonth) → newDate = base.
              const hasDaysRule = typeof inst.daysAfterStart === 'number';
              const hasMonthDayRule = !!inst.fixedDayOfMonth;
              if (!hasDaysRule && !hasMonthDayRule) return inst;

              let newFixed: string;
              if (hasMonthDayRule) {
                // Audit R-Feduzi: usa Date constructor (year, month, day) invece di
                // setMonth+setDate per evitare overflow. Esempio: base=31 gen + nextMonth
                // → setMonth(1) su day=31 fa rollare a 3 marzo, poi lastDay calcolato sul
                // mese sbagliato (marzo). Col constructor il mese resta corretto e clampiamo
                // il giorno al lastDay reale di quel mese (febbraio 28/29).
                const targetYear = base.getFullYear();
                const targetMonth = base.getMonth() + (inst.nextMonth ? 1 : 0);
                const lastDay = new Date(targetYear, targetMonth + 1, 0).getDate();
                const day = Math.min(inst.fixedDayOfMonth, lastDay);
                newFixed = new Date(targetYear, targetMonth, day).toISOString().split('T')[0];
              } else {
                const d = new Date(base);
                d.setDate(d.getDate() + (inst.daysAfterStart || 0));
                newFixed = d.toISOString().split('T')[0];
              }

              if (inst.fixedDate !== newFixed) {
                anyChanged = true;
                return { ...inst, fixedDate: newFixed };
              }
              return inst;
            });

            if (anyChanged) {
              // Audit R-Feduzi: capturiamo l'error per evitare silent failure.
              // Se l'update fallisce (RLS, connection, etc.) il counter NON deve incrementarsi.
              const { error: updateError } = await supabase
                .from('deal_services')
                .update({ payment_schedule: { ...ps, installments: newInstallments } })
                .eq('id', ds.id);
              if (updateError) {
                console.warn('[updateDeal] update deal_service fixedDate failed:', updateError);
                continue;
              }
              updatedCount++;
            }
          }

          if (updatedCount > 0) {
            console.log(`[updateDeal] Ricalcolate fixedDate su ${updatedCount} deal_services per nuova data base ${newBaseDate}`);
          }
        }
      } catch (err) {
        console.warn('[updateDeal] Errore ricalcolo fixedDate payment_schedule:', err);
      }

      // If services are provided, update them using smart diff (UPDATE existing, INSERT new, DELETE removed)
      // This preserves CASCADE children (renewal_tasks, invoice_queue, actual_service_costs, etc.)
      if (services) {
        const updateFlowId = `update-${Date.now()}`;
        console.log(`[${updateFlowId}] Starting smart service update for deal ${id} with ${services.length} services`);

        // 1. Fetch existing DB services to build diff
        const { data: existingDbServices } = await supabase
          .from('deal_services')
          .select('id, service_id, custom_service_name')
          .eq('deal_id', id);

        const existingIds = new Set(existingDbServices?.map(s => s.id) || []);

        // 2. Classify form services into update vs insert buckets
        const toUpdate: { formService: typeof services[0]; dbId: string }[] = [];
        const toInsert: typeof services[0][] = [];

        for (const formService of services) {
          // A form service has a real DB id if it's a valid UUID present in existing records
          const hasRealDbId = formService.id && existingIds.has(formService.id);
          if (hasRealDbId) {
            toUpdate.push({ formService, dbId: formService.id! });
          } else {
            toInsert.push(formService);
          }
        }

        // Services to delete: existing DB ids not referenced by any form service
        const formDbIds = new Set(toUpdate.map(item => item.dbId));
        const toDeleteIds = [...existingIds].filter(dbId => !formDbIds.has(dbId));

        console.log(`[${updateFlowId}] Diff: ${toUpdate.length} to update, ${toInsert.length} to insert, ${toDeleteIds.length} to delete`);

        // 3. Fetch existing variables for confirmation status preservation
        const allServiceIds = [...existingIds];
        let existingVariables: any[] = [];
        if (allServiceIds.length > 0) {
          const { data: variables } = await supabase
            .from('deal_variables')
            .select('id, name, type, amount, deal_service_id, is_confirmed, confirmed_at, confirmed_by')
            .in('deal_service_id', allServiceIds);
          existingVariables = variables || [];
        }

        const confirmationMap = new Map();
        existingVariables.forEach(v => {
          if (v.is_confirmed) {
            const key = `${v.name}-${v.type}-${v.amount}`;
            confirmationMap.set(key, {
              is_confirmed: v.is_confirmed,
              confirmed_at: v.confirmed_at,
              confirmed_by: v.confirmed_by
            });
          }
        });

        // 4. DELETE only removed services (CASCADE correctly cleans up their children)
        if (toDeleteIds.length > 0) {
          console.log(`[${updateFlowId}] Deleting ${toDeleteIds.length} removed services`);
          const { error: deleteError } = await supabase
            .from('deal_services')
            .delete()
            .in('id', toDeleteIds);
          if (deleteError) throw deleteError;
        }

        // Helper: prepare service data fields from a form service
        const prepareServiceData = (service: typeof services[0], index: number) => {
          const isCustomService = service.service_id?.toString().startsWith('custom-');
          // Riga prodotto magazzino: service_id sintetico "product-{uuid}-{ts}", non FK al catalogo servizi
          const isProductLine = (service as any).line_type === 'product'
            || service.service_id?.toString().startsWith('product-');

          let description = service.description;
          if (isCustomService && !description) {
            description = `Servizio personalizzato (ID: ${service.service_id})`;
          }

          // Metadata: per i prodotti sincronizziamo variation_id/price_list_id anche qui
          // come fallback di lettura legacy (le colonne dedicate sono la fonte canonica)
          let metadata = service.metadata || {};
          if (isCustomService) {
            const serviceType = service.custom_service_type || service.service?.service_type || 'one_time';
            metadata = { ...metadata, custom_service_type: serviceType };
          }
          if (isProductLine) {
            const variationId = (service as any).variation_id ?? metadata.variation_id ?? null;
            const priceListId = (service as any).price_list_id ?? metadata.price_list_id ?? null;
            metadata = {
              ...metadata,
              ...(variationId ? { variation_id: variationId } : {}),
              ...(priceListId ? { price_list_id: priceListId } : {}),
            };
          }

          const payload: any = {
            // Per le righe prodotto NON valorizziamo service_id (FK al catalogo servizi)
            service_id: isCustomService || isProductLine ? null : service.service_id,
            payment_method_id: service.payment_method_id,
            amount: service.amount,
            discounted_amount: service.discounted_amount,
            description: description,
            invoice_description: (service as any).invoice_description ?? null,
            payment_schedule: service.payment_schedule,
            monthly_duration: service.monthly_duration,
            custom_service_name: isCustomService ? (service.custom_service_name || 'Servizio personalizzato') : service.custom_service_name,
            // Categoria della riga scritta a mano: le righe da catalogo la ereditano
            // dal servizio, queste no. Senza, il cliente resta fuori dalle liste
            // per servizio acquistato.
            category_id: isCustomService ? ((service as any).category_id ?? null) : null,
            metadata: Object.keys(metadata).length > 0 ? metadata : null,
            order: index,
            // IVA per-riga: null = eredita da products/services.vat_rate (fallback 22)
            vat_rate: (service as any).vat_rate ?? null,
            // override_commission_scaling: colonna rimossa da deal_services col cutover
            // provvigioni V2 (P12); l'override della decurtazione vive su deals.
          };

          // Persistenza colonne prodotto — bug fix: in passato UPDATE non includeva questi campi
          if (isProductLine) {
            payload.line_type = 'product';
            payload.product_id = (service as any).product_id ?? null;
            payload.quantity = (service as any).quantity ?? null;
            payload.unit_of_measure = (service as any).unit_of_measure ?? 'pz';
            payload.unit_price = (service as any).unit_price ?? null;
            // NOTA 2026-06-06: variation_id/price_list_id richiedono migration
            // 20260605_deal_services_product_columns.sql non ancora applicata in EU.
            // I valori restano in `metadata` (sincronizzati sopra). Riabilitare le
            // 2 colonne dedicate SOLO dopo aver applicato la migration.
          } else {
            payload.line_type = 'service';
          }

          return payload;
        };

        // Helper: save variables for a deal_service, preserving confirmation status
        const saveVariablesForService = async (dealServiceId: string, formService: typeof services[0]) => {
          if (!formService.variables || formService.variables.length === 0) return;
          try {
            // Delete old variables for this service (they survived because no CASCADE on UPDATE)
            // Se la cancellazione fallisce o non toglie tutte le righe NON si reinserisce, come per
            // gli add-on qui sotto: prima l'esito non veniva letto e ogni salvataggio raddoppiava le
            // variabili, ognuna con la sua provvigione (Finup 28/09/2026: la stessa variabile
            // confermata è passata da 1 a 2 a 4 copie in due salvataggi).
            const { data: deletedVariables, error: deleteVariablesError } = await supabase
              .from('deal_variables')
              .delete()
              .eq('deal_service_id', dealServiceId)
              .select('id');
            const expectedDeleted = existingVariables.filter(v => v.deal_service_id === dealServiceId).length;
            if (deleteVariablesError || (deletedVariables?.length ?? 0) < expectedDeleted) {
              console.error(
                `[${updateFlowId}] Variabili del servizio ${dealServiceId} non cancellate (${deletedVariables?.length ?? 0}/${expectedDeleted}): non le ricreo per non duplicarle`,
                deleteVariablesError
              );
              return;
            }

            const { useDealVariableStore } = await import('./dealVariableStore');
            const { createServiceVariable } = useDealVariableStore.getState();

            for (const variable of formService.variables) {
              const variableKey = `${variable.name}-${variable.type}-${variable.amount}`;
              const confirmationStatus = confirmationMap.get(variableKey);

              const variableData = {
                name: variable.name,
                description: variable.description || '',
                amount: variable.amount ?? 0,
                type: variable.type || 'fixed',
                base_amount: variable.base_amount,
                is_cost: variable.is_cost ?? false,
                is_vat_exempt: variable.is_vat_exempt ?? false,
                is_stamp_duty: variable.is_stamp_duty ?? false,
                is_stamp_duty_art15: variable.is_stamp_duty_art15 ?? false,
                is_internal_cost: variable.is_internal_cost ?? false,
                include_in_pdf: variable.include_in_pdf ?? true,
                sort_order: variable.sort_order ?? 0,
                ...(confirmationStatus && {
                  is_confirmed: confirmationStatus.is_confirmed,
                  confirmed_at: confirmationStatus.confirmed_at,
                  confirmed_by: confirmationStatus.confirmed_by
                })
              };

              await createServiceVariable(dealServiceId, variableData);
            }
          } catch (error) {
            console.error(`[${updateFlowId}] Error saving variables for service ${dealServiceId}:`, error);
          }
        };

        // 5. UPDATE existing services in-place (preserves CASCADE children: renewal_tasks, invoice_queue, etc.)
        // Org id per lo snapshot add-on (deal_service_addons.organization_id è NOT NULL)
        const orgIdForAddons: string | undefined = current?.organization_id
          || (await getUserData().catch(() => null))?.organizationId;
        for (const { formService, dbId } of toUpdate) {
          const serviceData = prepareServiceData(formService, services.indexOf(formService));
          const { error: updateError } = await supabase
            .from('deal_services')
            .update(serviceData)
            .eq('id', dbId);

          if (updateError) {
            console.error(`[${updateFlowId}] Error updating service ${dbId}:`, updateError);
            throw updateError;
          }
          console.log(`[${updateFlowId}] Updated service ${dbId}`);

          await saveVariablesForService(dbId, formService);

          // Sync snapshot add-on: l'update in-place NON passa dal CASCADE, quindi
          // senza questo blocco le modifiche agli add-on fatte in edit venivano
          // perse in silenzio (bug 12/08). Riallineiamo deal_service_addons allo
          // stato del form: delete + reinsert. Solo se il form passa davvero il
          // campo addons — i caller programmatici che non lo passano (es. ricalcolo
          // importi) non devono cancellare lo snapshot esistente.
          if (Array.isArray(formService.addons) && orgIdForAddons) {
            const { error: addonsDeleteError } = await supabase
              .from('deal_service_addons')
              .delete()
              .eq('deal_service_id', dbId);
            if (addonsDeleteError) {
              console.error(`[${updateFlowId}] Error syncing add-ons for service ${dbId}:`, addonsDeleteError);
            } else if (formService.addons.length > 0) {
              await persistDealServiceAddons(dbId, orgIdForAddons, formService.addons);
              console.log(`[${updateFlowId}] Synced ${formService.addons.length} add-on(s) for service ${dbId}`);
            }
          }
        }

        // 6. INSERT new services
        for (const formService of toInsert) {
          const serviceData = {
            deal_id: id,
            ...prepareServiceData(formService, services.indexOf(formService))
          };

          const { data: serviceRecord, error: serviceError } = await supabase
            .from('deal_services')
            .insert(serviceData)
            .select()
            .single();

          if (serviceError) {
            console.error(`[${updateFlowId}] Error inserting service:`, serviceError);
            throw serviceError;
          }
          console.log(`[${updateFlowId}] Inserted new service ${serviceRecord?.id}`);

          // Persisti gli add-on selezionati per questa riga nuova (le righe
          // esistenti vengono sincronizzate nel blocco 5 con delete+reinsert).
          if (formService.addons && formService.addons.length > 0 && serviceRecord?.id) {
            await persistDealServiceAddons(serviceRecord.id, userData.organizationId, formService.addons);
            console.log(`[${updateFlowId}] Persisted ${formService.addons.length} add-on(s) for service ${serviceRecord.id}`);
          }

          await saveVariablesForService(serviceRecord.id, formService);
        }

        // 7. Update main deal's service_id based on the first service
        const firstService = services[0];
        if (firstService) {
          const isFirstServiceCustom = firstService.service_id?.toString().startsWith('custom-');
          const { error: updateMainDealError } = await supabase
            .from('deals')
            .update({
              service_id: isFirstServiceCustom ? null : firstService.service_id,
              // Vedi createDeal: il metodo scelto sull'offerta vince, altrimenti
              // resta la copia della prima riga com'era prima.
              payment_method_id: (deal as any).payment_method_id || firstService.payment_method_id
            })
            .eq('id', id);
          if (updateMainDealError) {
            console.error('Error updating main deal service info:', updateMainDealError);
          }
        }

        // 8. Update total amount, considering monthly duration
        const totalAmount = services.reduce((sum, service) => {
          const baseAmount = service.discounted_amount ?? service.amount;
          const isMonthlyService = service.service?.service_type === 'monthly';
          if (isMonthlyService && service.monthly_duration) {
            return sum + (baseAmount * service.monthly_duration);
          }
          return sum + baseAmount;
        }, 0);

        const { error: amountError } = await supabase
          .from('deals')
          .update({ amount: totalAmount })
          .eq('id', id);

        if (amountError) throw amountError;

        // Re-initialize renewals and template tasks for accepted deals after service changes
        const freshDeal = await get().fetchDealById(id);
        if (freshDeal && (freshDeal.status === 'accepted' || freshDeal.signed_at)) {
          // Complete orphaned renewal tasks for deleted deal_services (by toDeleteIds)
          if (toDeleteIds.length > 0) {
            for (const deletedDsId of toDeleteIds) {
              try {
                await supabase
                  .from('sales_activities')
                  .update({ status: 'completed', completed_at: new Date().toISOString() })
                  .like('description', `%[RENEWAL_TASK:${deletedDsId}]%`)
                  .eq('type', 'task')
                  .neq('status', 'completed');
              } catch (err) {
                console.error(`Error completing orphaned renewal tasks for deal_service ${deletedDsId}:`, err);
              }
            }
          }

          // Cleanup orphaned renewal tasks: complete any task whose deal_service_id no longer exists
          try {
            const { data: renewalTasks } = await supabase
              .from('sales_activities')
              .select('id, description')
              .eq('deal_id', id)
              .eq('type', 'task')
              .neq('status', 'completed')
              .like('description', '%[RENEWAL_TASK:%');

            if (renewalTasks && renewalTasks.length > 0) {
              const currentDsIds = new Set(
                (freshDeal.deal_services || []).map((ds: any) => ds.id)
              );

              for (const task of renewalTasks) {
                const match = task.description?.match(/\[RENEWAL_TASK:([a-f0-9-]+)\]/);
                if (match && match[1] && !currentDsIds.has(match[1])) {
                  await supabase
                    .from('sales_activities')
                    .update({ status: 'completed', completed_at: new Date().toISOString() })
                    .eq('id', task.id);
                  console.log(`Completed orphaned renewal task ${task.id} (deal_service ${match[1]} no longer exists)`);
                }
              }
            }
          } catch (err) {
            console.error('Error cleaning up orphaned renewal tasks:', err);
          }

          // Re-initialize renewals for new deal_services (safe: skips already initialized)
          try {
            await contractRenewalService.initializeRenewalsForDeal(id);
          } catch (err) {
            console.error('Error re-initializing renewals after service update:', err);
          }

          // Re-create template tasks for newly inserted deal_services
          if (toInsert.length > 0 && freshDeal.deal_services) {
            try {
              const { useServiceTaskTemplateStore } = await import('./serviceTaskTemplateStore');
              const { createTasksFromTemplates } = useServiceTaskTemplateStore.getState();
              for (const ds of freshDeal.deal_services) {
                if (ds.service_id && !formDbIds.has(ds.id)) {
                  try {
                    const tasksCreated = await createTasksFromTemplates(id, ds.id, ds.service_id, 'deal_signed');
                    if (tasksCreated > 0) {
                      console.log(`Created ${tasksCreated} template tasks for new deal_service ${ds.id}`);
                    }
                  } catch (taskErr) {
                    console.warn(`Failed to create template tasks for deal_service ${ds.id}:`, taskErr);
                  }
                }
              }
            } catch (err) {
              console.error('Error creating template tasks for new services:', err);
            }
          }
        }
      }

      // Fetch the complete updated deal
      const completeDeal = await get().fetchDealById(id);
      if (!completeDeal) throw new Error('Failed to fetch updated deal');
      
      // Trigger appropriate WhatsApp automation
      if (isStatusChange) {
        // Status change specific event
        triggerWhatsAppAutomation({
          entityType: 'deal',
          triggerEvent: 'status_change',
          entityData: completeDeal,
          oldStatus
        }).catch(err => console.error('Error triggering WhatsApp automation:', err));
      } else {
        // Generic update event
        triggerWhatsAppAutomation({
          entityType: 'deal',
          triggerEvent: 'update',
          entityData: completeDeal
        }).catch(err => console.error('Error triggering WhatsApp automation:', err));
      }

      // Trigger workflow automations for deal updates
      try {
        // Trigger deal_state_changed if status changed
        if (isStatusChange) {
          await workflowExecutionService.triggerWorkflows({
            entityType: 'deal',
            triggerType: 'deal_state_changed',
            entityId: id,
            triggerConfig: {
              from_status: oldStatus,
              to_status: completeDeal.status
            },
            triggerData: {
              client_id: completeDeal.client_id,
              lead_id: completeDeal.lead_id,
              total_value: completeDeal.total_value
            }
          });
        }

        // Trigger deal_signed if signed_at was set
        if (deal.signed_at && !current?.signed_at) {
          await workflowExecutionService.triggerWorkflows({
            entityType: 'deal',
            triggerType: 'deal_signed',
            entityId: id,
            triggerData: {
              signed_at: completeDeal.signed_at,
              status: completeDeal.status,
              total_value: completeDeal.total_value,
              client_id: completeDeal.client_id
            }
          });
        }

        // Emit deal:assigned event if assigned_to changed
        if (deal.assigned_to !== undefined && deal.assigned_to !== current?.assigned_to) {
          console.log('🔄 [DEAL STORE] Emitting deal:assigned event');
          eventBus.emitEvent({
            type: 'deal:assigned',
            payload: {
              dealId: id,
              title: completeDeal.title,
              fromUserId: current?.assigned_to,
              toUserId: completeDeal.assigned_to
            }
          });
        }

        // Emit deal:updated event for general field changes
        const updatedFields = Object.keys(deal).filter(key => !['id', 'updated_at'].includes(key));
        if (updatedFields.length > 0) {
          console.log('🔄 [DEAL STORE] Emitting deal:updated event');
          eventBus.emitEvent({
            type: 'deal:updated',
            payload: {
              dealId: id,
              title: completeDeal.title,
              fieldsChanged: updatedFields
            }
          });
        }
      } catch (workflowError) {
        console.error('Failed to trigger workflow automations:', workflowError);
        // Don't fail the update if workflow fails
      }

      // Sync deal contacts if provided
      if (contactIds !== undefined) {
        try {
          // Get user data for organization_id and created_by
          const userData = await getUserData();

          // Delete existing contacts
          const { error: deleteError } = await supabase
            .from('deal_contacts')
            .delete()
            .eq('deal_id', id);

          if (deleteError) {
            console.error('Error deleting existing deal contacts:', deleteError);
          }

          // Insert new contacts if any (modello nuovo: person_client_id = id persona)
          if (contactIds.length > 0 && userData?.organizationId) {
            const contactRecords = contactIds.map(personId => ({
              deal_id: id,
              person_client_id: personId,
              organization_id: userData.organizationId,
              created_by: userData.userId
            }));

            const { error: insertError } = await supabase
              .from('deal_contacts')
              .insert(contactRecords);

            if (insertError) {
              console.error('Error inserting deal contacts:', insertError);
            } else {
              console.log(`✅ Successfully synced ${contactIds.length} contacts for deal ${id}`);
            }
          }
        } catch (contactsError) {
          console.error('Error syncing deal contacts:', contactsError);
          // Don't fail the update if contacts sync fails
        }
      }

      const deals = get().deals.map(d =>
        d.id === id ? completeDeal : d
      );
      set({ deals });
      return completeDeal;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  getAssociatedProjects: async (dealId: string) => {
    try {
      const { data: projects, error } = await supabase
        .from('projects')
        .select('*')
        .eq('deal_id', dealId);

      if (error) throw error;
      return projects || [];
    } catch (error) {
      console.error('Error fetching associated projects:', error);
      return [];
    }
  },

  deleteDeal: async (id: string, deleteAssociatedProjects: boolean = false, options: { deleteDraftOrders?: boolean } = {}) => {
    try {
      // Get the deal before deleting for the trigger
      const { data: dealData } = await supabase
        .from('deals')
        .select(`
          *,
          client:clients (
            id,
            business_name
          )
        `)
        .eq('id', id)
        .single();

      // If deleteAssociatedProjects is true, delete all associated projects first
      if (deleteAssociatedProjects) {
        const { error: projectDeleteError } = await supabase
          .from('projects')
          .delete()
          .eq('deal_id', id);

        if (projectDeleteError) {
          console.error('Error deleting associated projects:', projectDeleteError);
          throw new Error('Impossibile eliminare i progetti associati');
        }
      } else {
        // Otherwise, unlink projects by setting deal_id to null
        const { error: projectUnlinkError } = await supabase
          .from('projects')
          .update({ deal_id: null })
          .eq('deal_id', id);

        if (projectUnlinkError) {
          console.error('Error unlinking associated projects:', projectUnlinkError);
          throw new Error('Impossibile scollegare i progetti associati');
        }
      }

      // Ordini di magazzino collegati: il vincolo orders.deal_id impedisce di
      // eliminare l'offerta finché esistono. Si scollegano tutti (nota interna con
      // il titolo dell'offerta); le bozze da eliminare si eliminano dopo, a offerta
      // eliminata. Se l'eliminazione dell'offerta fallisce si ricollegano.
      const { data: linkedOrders, error: linkedErr } = await supabase
        .from('orders')
        .select('id, order_number, status, internal_notes')
        .eq('deal_id', id);
      if (linkedErr) throw linkedErr;
      const orders = (linkedOrders ?? []) as Array<{ id: string; order_number: string; status: string; internal_notes: string | null }>;
      const unlinked: typeof orders = [];
      const relink = async () => {
        for (const o of unlinked) {
          await supabase.from('orders').update({ deal_id: id, internal_notes: o.internal_notes }).eq('id', o.id);
        }
      };
      const deletionNote = `Offerta «${dealData?.title ?? id}» eliminata il ${new Date().toLocaleDateString('it-IT')}`;
      for (const o of orders) {
        const { error: unlinkErr } = await supabase
          .from('orders')
          .update({
            deal_id: null,
            internal_notes: [o.internal_notes, deletionNote].filter(Boolean).join('\n'),
            updated_at: new Date().toISOString(),
          })
          .eq('id', o.id);
        if (unlinkErr) {
          console.error('Error unlinking order from deal:', unlinkErr);
          await relink();
          throw new Error(`Impossibile scollegare l'ordine ${o.order_number} dall'offerta`);
        }
        unlinked.push(o);
      }

      const { error } = await supabase
        .from('deals')
        .delete()
        .eq('id', id);

      if (error) {
        await relink();
        throw error;
      }

      const draftOrdersNotDeleted: string[] = [];
      if (options.deleteDraftOrders) {
        for (const o of orders.filter(x => x.status === 'draft')) {
          const { error: orderDeleteError } = await supabase
            .from('orders')
            .delete()
            .eq('id', o.id)
            .eq('status', 'draft');
          if (orderDeleteError) {
            console.error('Error deleting draft order of deleted deal:', orderDeleteError);
            draftOrdersNotDeleted.push(o.order_number);
          }
        }
      }

      // Trigger WhatsApp automation for deletion
      if (dealData) {
        triggerWhatsAppAutomation({
          entityType: 'deal',
          triggerEvent: 'delete',
          entityData: dealData
        }).catch(err => console.error('Error triggering WhatsApp automation:', err));
      }

      const deals = get().deals.filter(d => d.id !== id);
      set({ deals });
      return draftOrdersNotDeleted;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      throw error;
    }
  },

  handleDealSigned: async (dealId: string) => {
    try {
      const deal = await get().fetchDealById(dealId);
      if (!deal) throw new Error('Deal not found');

      // Flag per-org: se la generazione di progetti + task da template è gestita dal
      // workflow (step create_projects_from_services su deal_signed), NON crearli qui,
      // altrimenti si creano DOPPIONI in race con il workflow (bug doppi progetti).
      let generationViaWorkflow = false;
      try {
        if (deal.organization_id) {
          const { data: genFlag } = await supabase
            .from('settings')
            .select('deal_accept_generation_via_workflow')
            .eq('organization_id', deal.organization_id)
            .maybeSingle();
          generationViaWorkflow = genFlag?.deal_accept_generation_via_workflow === true;
        }
      } catch (flagErr) {
        console.warn('handleDealSigned: could not read deal_accept_generation_via_workflow flag:', flagErr);
      }

      // Create a project for each service (if not already exists) — saltato se la
      // generazione è gestita dal workflow.
      if (!generationViaWorkflow && deal.deal_services && deal.deal_services.length > 0) {
        // Guard anti-doppioni. Il collegamento progetto→offerta esiste in DUE forme:
        //   - colonna projects.deal_id  → scritta dallo step workflow
        //     create_projects_from_services (flusia-core), 722 progetti
        //   - metadata.deal_id          → scritta storicamente da qui, 253 progetti
        // Guardare una sola forma rende il controllo cieco sull'altra e ricrea i
        // progetti già esistenti. Si leggono entrambe.
        const [byColumn, byMetadata] = await Promise.all([
          supabase.from('projects').select('metadata').eq('deal_id', deal.id).eq('archived', false),
          supabase.from('projects').select('metadata').eq('metadata->>deal_id', deal.id).eq('archived', false),
        ]);
        const existingProjects = [...(byColumn.data ?? []), ...(byMetadata.data ?? [])];

        // Anche la chiave del guard esiste in due forme: deal_service_id (nuova,
        // regge pure i servizi personalizzati che non hanno service_id) e
        // service_id (vecchia). Si tiene conto di entrambe.
        const existingDealServiceIds = new Set(
          existingProjects.map(p => (p.metadata as { deal_service_id?: string })?.deal_service_id).filter(Boolean)
        );
        const existingServiceIds = new Set(
          existingProjects.map(p => (p.metadata as { service_id?: string })?.service_id).filter(Boolean)
        );

        // Filter services that don't have a project yet
        const servicesToCreate = deal.deal_services.filter(
          ds => ds.service?.id
            && !existingDealServiceIds.has(ds.id)
            && !existingServiceIds.has(ds.service.id)
        );

        if (servicesToCreate.length > 0) {
          const projectPromises = servicesToCreate.map(dealService => {
            return supabase
              .from('projects')
              .insert({
                name: dealService.service?.title,
                description: dealService.service?.description || '',
                client_id: deal.client_id,
                // Colonna + metadata: la colonna è la forma che usa il workflow ed è
                // quella su cui si allineano le letture; il metadata resta per non
                // rompere il codice che lo legge ancora.
                deal_id: deal.id,
                organization_id: deal.organization_id,
                status: 'planning',
                objectives: [],
                metadata: {
                  deal_id: deal.id,
                  service_id: dealService.service?.id,
                  deal_service_id: dealService.id,
                  service_type: dealService.service?.service_type
                }
              })
              .select();
          });

          await Promise.all(projectPromises);
        }
      }

      // Initialize contract renewals for recurring services
      try {
        await contractRenewalService.initializeRenewalsForDeal(dealId);
        console.log('Contract renewals initialized for deal:', dealId);
      } catch (renewalError) {
        // Non-blocking error - log but don't fail the entire operation
        console.error('Error initializing renewals:', renewalError);
      }

      // Create tasks from templates for deal_signed trigger — saltato se la
      // generazione è gestita dal workflow (evita task duplicati).
      if (!generationViaWorkflow && deal.deal_services) {
        try {
          const { useServiceTaskTemplateStore } = await import('./serviceTaskTemplateStore');
          const { createTasksFromTemplates } = useServiceTaskTemplateStore.getState();

          for (const dealService of deal.deal_services) {
            if (dealService.service_id) {
              try {
                const tasksCreated = await createTasksFromTemplates(
                  dealId,
                  dealService.id,
                  dealService.service_id,
                  'deal_signed'
                );

                if (tasksCreated > 0) {
                  console.log(`✅ Created ${tasksCreated} tasks from templates for deal service ${dealService.id} on signing`);
                }
              } catch (taskError) {
                console.warn('⚠️ Failed to create tasks from templates on signing:', taskError);
              }
            } else {
              console.warn(`⚠️ Deal service has no service_id - skipping task creation`);
            }
          }
        } catch (taskTemplateError) {
          console.error('❌ Error loading service task template store for signing:', taskTemplateError);
        }
      }

      // Emit event for workflow triggers
      try {
        const totalValue = calculateDealTotal(deal);
        console.log('🔄 [DEAL STORE] Emitting deal:signed event');
        eventBus.emitEvent({
          type: 'deal:signed',
          payload: {
            dealId: deal.id,
            title: deal.title,
            totalValue,
            clientId: deal.client_id,
            signedAt: new Date().toISOString()
          }
        });
      } catch (eventError) {
        console.error('Error emitting deal:signed event:', eventError);
        // Don't fail the signing if event fails
      }
    } catch (error) {
      set({ error: friendlyDbError(error) });
      throw error;
    }
  },

  downloadSignedDealPDF: async (deal: Deal) => {
    if (!deal.signed_pdf_url) {
      throw new Error('No signed PDF found for this deal');
    }

    try {
      // Download the PDF from storage
      const response = await fetch(deal.signed_pdf_url);
      if (!response.ok) throw new Error('Failed to download PDF');
      
      const blob = await response.blob();
      
      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `deal-${deal.id}.pdf`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      window.URL.revokeObjectURL(url);
    } catch (error) {
      throw error;
    }
  },
  
  updateDealStatus: async (id: string, status: string, note?: string, options?: { skipNote?: boolean }) => {
    set({ statusUpdateLoading: true, error: null });
    try {
      // First check if the deal has an organization_id and get current data for logging
      const { data: currentDeal } = await supabase
        .from('deals')
        .select('organization_id, status, title, client_id')
        .eq('id', id)
        .single();

      const updateData: any = {
        status,
        updated_at: new Date().toISOString(),
        // If status is 'accepted', set closed_at and signed_at dates
        ...(status === 'accepted' ? {
          closed_at: new Date().toISOString(),
          signed_at: new Date().toISOString()
        } : {})
      };

      // If organization_id is missing, get it from the user profile
      if (!currentDeal?.organization_id) {
        // Get the user's organization using the centralized utility
        try {
          const userData = await getUserData();
          if (userData?.organizationId) {
            updateData.organization_id = userData.organizationId;
          }
        } catch (err) {
          console.error('Error getting organization ID:', err);
          // Continue without organization_id, the API call might still succeed
        }
      }

      const { data, error } = await supabase
        .from('deals')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      // If a note was provided, add it as a deal note (a meno che il chiamante non la
      // scriva da sé — `options.skipNote`, vedi commento sull'interfaccia).
      if (note && note.trim() && !options?.skipNote) {
        try {
          const now = new Date().toISOString();

          // Use the dedicated method to add a note
          await get().addDealNote(id, note, {
            status_change: {
              from: currentDeal?.status || 'unknown',
              to: status,
              date: now
            }
          });
        } catch (noteErr) {
          console.error('Error adding deal note:', noteErr);
        }
      }

      const deals = get().deals.map(d =>
        d.id === id ? { ...d, ...data } : d
      );

      set({ deals });

      // Fetch the complete deal data before emitting event
      const completeDeal = await get().fetchDealById(id);
      if (completeDeal) {
        // Emit event for workflow triggers
        try {
          console.log('🔄 [DEAL STORE] Emitting deal:state_changed event');
          eventBus.emitEvent({
            type: 'deal:state_changed',
            payload: {
              dealId: completeDeal.id,
              fromStatus: currentDeal?.status,
              toStatus: status,
              title: completeDeal.title,
              clientId: completeDeal.client_id,
              leadId: completeDeal.lead_id,
              totalValue: completeDeal.total_value
            }
          });
        } catch (eventError) {
          console.error('Error emitting event:', eventError);
          // Non fallire l'update del deal se l'evento fallisce
        }

        // Trigger WhatsApp automation for status change with complete deal data
        triggerWhatsAppAutomation({
          entityType: 'deal',
          triggerEvent: 'status_change',
          entityData: completeDeal,
          oldStatus: currentDeal?.status
        }).catch(err => console.error('Error triggering WhatsApp automation:', err));
      } else {
        console.error('Failed to fetch complete deal data for WhatsApp automation');
      }

      // If deal status is changed to 'accepted', generate invoice queue entries and copy template costs
      if (status === 'accepted') {
        try {
          // Flag per-org: se la generazione progetti + coda fatturazione è gestita
          // da workflow (step create_projects_from_services / generate_invoice_queue
          // su deal_won/deal_signed), il frontend NON li crea (evita doppio binario).
          let generationViaWorkflow = false;
          try {
            const orgIdForFlag = completeDeal?.organization_id || currentDeal?.organization_id;
            if (orgIdForFlag) {
              const { data: genFlag } = await supabase
                .from('settings')
                .select('deal_accept_generation_via_workflow')
                .eq('organization_id', orgIdForFlag)
                .maybeSingle();
              generationViaWorkflow = genFlag?.deal_accept_generation_via_workflow === true;
            }
          } catch (flagErr) {
            console.warn('Could not read deal_accept_generation_via_workflow flag:', flagErr);
          }

          // Copy template costs to actual service costs table
          const { data: copiedCosts, error: copyError } = await supabase.rpc('copy_template_costs_to_actual', {
            p_deal_id: id
          });

          if (copyError) {
            console.error('Error copying template costs to actual costs:', copyError);
          } else {
            console.log(`Copied ${copiedCosts || 0} template costs to actual costs for deal ${id}`);
          }

          // Trigger commission calculation for the month of the closed deal
          try {
            console.log(`🔄 Triggering commission calculation for deal ${id}...`);

            // Get organization_id from the deal
            const orgId = completeDeal?.organization_id || currentDeal?.organization_id;
            if (orgId && updateData.closed_at) {
              // Get current user ID
              const { data: { user } } = await supabase.auth.getUser();
              const userId = user?.id;

              // Import the service dynamically to avoid circular dependency
              const { commissionCacheService } = await import('../services/commissionCacheService');

              // Trigger calculation for the month when deal was closed
              const closedDate = new Date(updateData.closed_at);
              await commissionCacheService.triggerCalculation(
                orgId,
                closedDate,
                userId, // Pass actual user ID, not a string
                false // Don't force recalculation, just calculate if needed
              );

              console.log(`✅ Commission calculation triggered for deal ${id}`);
            } else {
              console.warn(`⚠️ Could not trigger commission calculation: missing orgId or closed_at`);
            }
          } catch (commissionError) {
            console.error('❌ Error triggering commission calculation:', commissionError);
            // Non-blocking: le provvigioni possono essere calcolate manualmente dopo
          }

          // Initialize contract renewals for all services when deal is accepted
          try {
            console.log(`[DEAL STORE] Starting contract renewals initialization for deal ${id}...`);
            await contractRenewalService.initializeRenewalsForDeal(id);
            console.log(`[DEAL STORE] ✅ Contract renewals initialized for deal: ${id}`);
          } catch (renewalError) {
            console.error(`[DEAL STORE] ❌ Error initializing contract renewals for deal ${id}:`, renewalError);
          }

          // Generate enhanced invoice queue entries (SKIP se gestito da workflow)
          if (!generationViaWorkflow) {
            // Import here to avoid circular dependency
            const { enhancedInvoiceQueueService } = await import('../services/enhancedInvoiceQueueService');
            // Generate enhanced invoice queue entries in background with full FattureInCloud support
            enhancedInvoiceQueueService.generateEnhancedQueueItemsForDeal(id)
              .then((itemsCreated: number) => {
                if (itemsCreated > 0) {
                  console.log(`Generated ${itemsCreated} enhanced invoice queue items for deal ${id}`);
                } else {
                  console.log(`No invoice queue items were created for deal ${id} (no services with auto_invoice enabled)`);
                }
              })
              .catch((err: Error) => {
                console.error(`Error generating enhanced invoice queue items for deal ${id}:`, err);
              });
          } else {
            console.log(`[DEAL STORE] Skip invoice queue gen for deal ${id} — gestito da workflow`);
          }

          // Create projects for each service when deal is accepted (SKIP se gestito da workflow)
          if (!generationViaWorkflow && completeDeal?.deal_services && completeDeal.deal_services.length > 0) {
            try {
              console.log(`🏗️ Creating projects for ${completeDeal.deal_services.length} services in deal ${id}`);

              // Check existing projects for this deal to avoid duplicates (only non-archived)
              const { data: existingProjects } = await supabase
                .from('projects')
                .select('metadata')
                .eq('deal_id', id)
                .eq('archived', false);

              // Il guard va tenuto su deal_service_id, non su service_id: per i servizi
              // personalizzati service_id è null, filter(Boolean) lo scarta e has(null)
              // è sempre false, quindi il progetto veniva ricreato a ogni passaggio.
              // Stessa correzione già applicata nello step workflow del core.
              const existingDealServiceIds = new Set(
                existingProjects?.map(p => (p.metadata as { deal_service_id?: string })?.deal_service_id).filter(Boolean) || []
              );
              const existingServiceIds = new Set(
                existingProjects?.map(p => (p.metadata as { service_id?: string })?.service_id).filter(Boolean) || []
              );

              // Get user data for organization_id
              const userData = await getUserData();
              if (!userData?.organizationId) throw new Error('User organization not found');

              // Filter services that don't have a project yet
              const servicesToCreate = completeDeal.deal_services.filter(
                ds => !existingDealServiceIds.has(ds.id)
                  && !(ds.service_id && existingServiceIds.has(ds.service_id))
              );

              if (servicesToCreate.length === 0) {
                console.log(`⏭️ All projects already exist for deal ${id}, skipping creation`);
              } else {
                const projectPromises = servicesToCreate.map(dealService => {
                  const projectName = dealService.service?.title || dealService.custom_service_name || 'Progetto Personalizzato';
                  const projectDescription = dealService.service?.description || dealService.description || '';

                  return supabase
                    .from('projects')
                    .insert({
                      name: projectName,
                      description: projectDescription,
                      client_id: completeDeal.client_id,
                      organization_id: userData.organizationId,
                      deal_id: id,
                      status: 'planning',
                      objectives: [],
                      metadata: {
                        service_id: dealService.service_id,
                        deal_service_id: dealService.id,
                        service_type: dealService.service?.service_type
                      }
                    })
                    .select();
                });

                const projectResults = await Promise.all(projectPromises);
                const projectsCreated = projectResults.filter(result => result.data && result.data.length > 0).length;
                console.log(`✅ Created ${projectsCreated} projects for deal ${id}`);

                // Create tasks from templates for deal_signed trigger (fire-and-forget per non bloccare UI)
                get().createTasksFromTemplates(id, completeDeal, projectResults)
                  .then(() => console.log(`✅ Task creation completed in background for deal ${id}`))
                  .catch(err => console.error(`❌ Task creation failed for deal ${id}:`, err));

                // Create Google Drive folders for projects if automatic folder creation is enabled
                try {
                  // Get organization settings to check if Drive folder creation is enabled
                  const { data: orgSettings } = await supabase
                    .from('settings')
                    .select('drive_settings, sync_drive')
                    .eq('organization_id', userData.organizationId)
                    .single();

                  if (orgSettings?.sync_drive && orgSettings?.drive_settings?.create_folders_automatically) {
                    const driveSettings = orgSettings.drive_settings;

                    // Only proceed if we have an admin user configured and a root folder
                    if (driveSettings.drive_admin_user_id && driveSettings.root_folder_id) {
                      console.log(`📁 Creating Google Drive folders for ${projectResults.length} projects...`);

                      // Get client info for folder naming
                      const { data: clientData } = await supabase
                        .from('clients')
                        .select('business_name, client_code')
                        .eq('id', completeDeal.client_id)
                        .single();

                      const clientName = clientData?.business_name || 'Cliente';

                      for (const projectResult of projectResults) {
                        if (projectResult.data && projectResult.data[0]) {
                          const project = projectResult.data[0];

                          try {
                            // Create folder via edge function using admin's credentials
                            const { data: folderData, error: folderError } = await supabase.functions.invoke('google-drive-operations', {
                              body: {
                                operation: 'createFolder',
                                organization_id: userData.organizationId,
                                user_id: driveSettings.drive_admin_user_id, // Use admin's account
                                folder_name: `${clientName} - ${project.name}`,
                                parent_id: driveSettings.root_folder_id,
                                folder_type: 'root',
                                entity_id: project.id,
                                entity_type: 'project'
                              }
                            });

                            if (!folderError && folderData?.folder) {
                              // Update project metadata with the created folder
                              await supabase
                                .from('projects')
                                .update({
                                  metadata: {
                                    ...project.metadata,
                                    drive_folder_id: folderData.folder.id,
                                    drive_folder_name: folderData.folder.name
                                  }
                                })
                                .eq('id', project.id);

                              console.log(`✅ Created Drive folder for project ${project.name}: ${folderData.folder.id}`);
                            } else {
                              console.error(`❌ Failed to create Drive folder for project ${project.id}:`, folderError);
                            }
                          } catch (driveFolderError) {
                            console.error(`❌ Error creating Drive folder for project ${project.id}:`, driveFolderError);
                          }
                        }
                      }
                    } else {
                      console.log(`⏭️ Skipping Drive folder creation: admin not configured or root folder not set`);
                    }
                  }
                } catch (driveError) {
                  console.error('❌ Error in Drive folder creation process:', driveError);
                  // Non-blocking: projects are still created, folders can be created manually
                }
              }

            } catch (projectError) {
              console.error('❌ Error creating projects for deal:', projectError);
            }
          }
        } catch (err) {
          console.error('Error loading invoice queue service:', err);
        }
      }

      set({ statusUpdateLoading: false });
      return data;
    } catch (error) {
      set({ error: friendlyDbError(error), statusUpdateLoading: false });
      return null;
    } finally {
      set({ statusUpdateLoading: false });
    }
  },

  // Contract renewal methods
  getExpiringContracts: async (daysAhead = 30) => {
    try {
      return await contractRenewalService.getExpiringContracts(daysAhead);
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return [];
    }
  },

  updateContractRenewal: async (dealServiceId: string, renewalData: any, dealId?: string) => {
    try {
      return await contractRenewalService.processContractRenewal(dealServiceId, { ...renewalData, dealId });
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return false;
    }
  },

  createRenewalTasks: async () => {
    try {
      return await contractRenewalService.createRenewalTasks();
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return 0;
    }
  },

  duplicateDeal: async (dealId: string, customTitle?: string) => {
    try {
      const originalDeal = await get().fetchDealById(dealId);
      if (!originalDeal) throw new Error('Deal not found');

      // R06: leggi fresh il client.assigned_to invece di copiare quello del deal vecchio.
      // Caso d'uso: l'utente aveva creato l'offerta con un assignee, poi ha corretto
      // l'anagrafica cliente cambiando assigned_to. La nuova offerta (duplicata) deve
      // riflettere il valore aggiornato del cliente, non quello "fotografato" nel deal.
      let freshAssignedTo: string | null = originalDeal.assigned_to ?? null;
      if (originalDeal.client_id) {
        try {
          const { data: clientFresh } = await supabase
            .from('clients')
            .select('assigned_to')
            .eq('id', originalDeal.client_id)
            .maybeSingle();
          // Audit R06: distinguiamo null intenzionale (assignee rimosso) da undefined
          // (campo non presente nella response). Solo undefined fa fallback al valore
          // originale del deal.
          if (clientFresh && clientFresh.assigned_to !== undefined) {
            freshAssignedTo = clientFresh.assigned_to;
          }
        } catch (err) {
          console.warn('[duplicateDeal] fresh assigned_to lookup failed, falling back to original:', err);
        }
      }

      // Create new deal with same data
      const newDealData = {
        title: customTitle || `${originalDeal.title} (Copia)`,
        description: originalDeal.description,
        client_id: originalDeal.client_id,
        amount: originalDeal.amount,
        status: 'draft',
        assigned_to: freshAssignedTo,
        template_id: originalDeal.template_id,
        custom_pdf_field: originalDeal.custom_pdf_field
      };

      // Prepare services data
      // Le righe prodotto vivono nelle colonne dedicate (line_type, product_id,
      // quantita', unita', prezzo unitario): senza copiarle, la copia degradava
      // l'articolo a un servizio senza prezzo ne' quantita'. `metadata` va
      // portato appresso perche' custodisce variation_id e price_list_id, le cui
      // colonne dedicate non sono ancora applicate in produzione.
      const servicesData = originalDeal.deal_services?.map(service => ({
        service_id: service.service_id,
        amount: service.amount,
        discounted_amount: service.discounted_amount,
        description: service.description,
        invoice_description: (service as any).invoice_description,
        payment_schedule: service.payment_schedule,
        payment_method_id: service.payment_method_id,
        monthly_duration: service.monthly_duration,
        custom_service_name: service.custom_service_name,
        metadata: (service as any).metadata,
        vat_rate: (service as any).vat_rate,
        line_type: (service as any).line_type,
        product_id: (service as any).product_id,
        quantity: (service as any).quantity,
        unit_of_measure: (service as any).unit_of_measure,
        unit_price: (service as any).unit_price,
      })) || [];

      // Create the new deal
      return await get().createDeal(newDealData, servicesData);
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  // Calculate deal total including service costs with markup
  calculateDealTotalWithCosts: async (dealId: string) => {
    try {
      // Get deal services total
      const { data: dealServices, error: servicesError } = await supabase
        .from('deal_services')
        .select('amount, discounted_amount, monthly_duration, service:services(service_type)')
        .eq('deal_id', dealId);

      if (servicesError) throw servicesError;

      const serviceTotal = dealServices?.reduce((sum, service) => {
        const baseAmount = service.discounted_amount ?? service.amount;
        const isMonthlyService = service.service?.service_type === 'monthly';
        
        if (isMonthlyService && service.monthly_duration) {
          return sum + (baseAmount * service.monthly_duration);
        }
        
        return sum + baseAmount;
      }, 0) || 0;

      // Get costs total (only client_price for billable costs with markup that are visible to client)
      const { data: costs, error: costsError } = await supabase
        .from('actual_service_costs')
        .select('client_price, estimated_amount, actual_amount, is_billable, has_markup, is_visible_to_client')
        .eq('deal_id', dealId)
        .eq('is_billable', true)
        .eq('is_visible_to_client', true);

      if (costsError) throw costsError;

      const costTotal = costs?.reduce((sum, cost) => {
        // Use client_price if markup is applied, otherwise use base amount
        const costAmount = cost.has_markup && cost.client_price 
          ? cost.client_price 
          : (cost.actual_amount || cost.estimated_amount);
        
        return sum + costAmount;
      }, 0) || 0;

      return {
        serviceTotal,
        costTotal,
        grandTotal: serviceTotal + costTotal
      };
    } catch (error) {
      console.error('Error calculating deal total with costs:', error);
      return null;
    }
  },

  // Calculate detailed totals (client visible vs internal costs)
  calculateDealTotalsDetailed: async (dealId: string) => {
    try {
      // Get deal services total
      const { data: dealServices, error: servicesError } = await supabase
        .from('deal_services')
        .select('amount, discounted_amount, monthly_duration, service:services(service_type)')
        .eq('deal_id', dealId);

      if (servicesError) throw servicesError;

      const serviceTotal = dealServices?.reduce((sum, service) => {
        const baseAmount = service.discounted_amount ?? service.amount;
        const isMonthlyService = service.service?.service_type === 'monthly';
        
        if (isMonthlyService && service.monthly_duration) {
          return sum + (baseAmount * service.monthly_duration);
        }
        
        return sum + baseAmount;
      }, 0) || 0;

      // Get ALL costs for internal calculation
      const { data: allCosts, error: allCostsError } = await supabase
        .from('actual_service_costs')
        .select('client_price, estimated_amount, actual_amount, is_billable, has_markup, is_visible_to_client')
        .eq('deal_id', dealId);

      if (allCostsError) throw allCostsError;

      // Calculate visible costs (what client sees)
      const visibleCostTotal = allCosts?.filter(cost => 
        cost.is_billable && cost.is_visible_to_client
      ).reduce((sum, cost) => {
        const costAmount = cost.has_markup && cost.client_price 
          ? cost.client_price 
          : (cost.actual_amount || cost.estimated_amount);
        return sum + costAmount;
      }, 0) || 0;

      // Calculate total internal costs (including hidden ones)
      const totalInternalCosts = allCosts?.reduce((sum, cost) => {
        const costAmount = cost.actual_amount || cost.estimated_amount;
        return sum + costAmount;
      }, 0) || 0;

      const clientTotal = serviceTotal + visibleCostTotal;
      const internalTotal = serviceTotal + totalInternalCosts;
      const marginAmount = clientTotal - internalTotal;
      const marginPercentage = clientTotal > 0 ? (marginAmount / clientTotal) * 100 : 0;

      return {
        clientTotal,      // Quello che vede il cliente (servizi + costi visibili con markup)
        internalTotal,    // Costo reale interno (servizi + tutti i costi base)  
        marginAmount,     // Margine in euro
        marginPercentage  // Margine in percentuale
      };
    } catch (error) {
      console.error('Error calculating detailed deal totals:', error);
      return null;
    }
  },

  // Get comprehensive cost breakdown for a deal
  getDealCostBreakdown: async (dealId: string) => {
    try {
      // Get deal services
      const { data: dealServices, error: servicesError } = await supabase
        .from('deal_services')
        .select(`
          *,
          service:services(id, title, service_type)
        `)
        .eq('deal_id', dealId);

      if (servicesError) throw servicesError;

      // Get costs grouped by service and category
      const { data: costs, error: costsError } = await supabase
        .from('actual_service_costs')
        .select(`
          *,
          cost_category:service_cost_categories(name, category_type, allows_markup),
          deal_service:deal_services(id, service_id)
        `)
        .eq('deal_id', dealId);

      if (costsError) throw costsError;

      // Calculate breakdown
      const breakdown = {
        services: dealServices?.map(service => {
          const serviceCosts = costs?.filter(cost => 
            cost.deal_service?.service_id === service.service_id
          ) || [];

          const costBreakdown = {
            totalBaseCost: 0,
            totalMarkup: 0,
            totalClientPrice: 0,
            costsByCategory: {} as Record<string, any>
          };

          serviceCosts.forEach(cost => {
            const baseAmount = cost.actual_amount || cost.estimated_amount;
            const clientPrice = cost.client_price || baseAmount;
            const markup = clientPrice - baseAmount;

            costBreakdown.totalBaseCost += baseAmount;
            costBreakdown.totalMarkup += markup;
            if (cost.is_billable) {
              costBreakdown.totalClientPrice += clientPrice;
            }

            const categoryType = cost.cost_category?.category_type || 'other';
            if (!costBreakdown.costsByCategory[categoryType]) {
              costBreakdown.costsByCategory[categoryType] = {
                baseCost: 0,
                markup: 0,
                clientPrice: 0,
                count: 0
              };
            }

            costBreakdown.costsByCategory[categoryType].baseCost += baseAmount;
            costBreakdown.costsByCategory[categoryType].markup += markup;
            if (cost.is_billable) {
              costBreakdown.costsByCategory[categoryType].clientPrice += clientPrice;
            }
            costBreakdown.costsByCategory[categoryType].count += 1;
          });

          const baseAmount = service.discounted_amount ?? service.amount;
          const isMonthlyService = service.service?.service_type === 'monthly';
          const serviceTotal = isMonthlyService && service.monthly_duration 
            ? baseAmount * service.monthly_duration 
            : baseAmount;

          return {
            ...service,
            serviceTotal,
            costs: serviceCosts,
            costBreakdown
          };
        }) || [],
        
        totals: {
          servicesTotal: 0,
          costsBaseTotalTotal: 0,
          costsMarkupTotal: 0,
          costsClientTotal: 0,
          grandTotal: 0
        }
      };

      // Calculate totals
      breakdown.totals.servicesTotal = breakdown.services.reduce((sum, service) => 
        sum + service.serviceTotal, 0
      );
      
      breakdown.totals.costsBaseTotalTotal = breakdown.services.reduce((sum, service) => 
        sum + service.costBreakdown.totalBaseCost, 0
      );
      
      breakdown.totals.costsMarkupTotal = breakdown.services.reduce((sum, service) => 
        sum + service.costBreakdown.totalMarkup, 0
      );
      
      breakdown.totals.costsClientTotal = breakdown.services.reduce((sum, service) => 
        sum + service.costBreakdown.totalClientPrice, 0
      );
      
      breakdown.totals.grandTotal = breakdown.totals.servicesTotal + breakdown.totals.costsClientTotal;

      return breakdown;
    } catch (error) {
      console.error('Error getting deal cost breakdown:', error);
      return null;
    }
  },

  // Recalculate deal amount including costs
  recalculateDealAmountWithCosts: async (dealId: string) => {
    try {
      const totals = await get().calculateDealTotalWithCosts(dealId);
      if (!totals) return false;

      // Update deal amount
      const { error } = await supabase
        .from('deals')
        .update({ 
          amount: totals.grandTotal,
          updated_at: new Date().toISOString()
        })
        .eq('id', dealId);

      if (error) throw error;

      // Refresh deals
      await get().fetchDeals();
      return true;
    } catch (error) {
      console.error('Error recalculating deal amount with costs:', error);
      return false;
    }
  },

  // Create tasks from templates when deal is accepted
  createTasksFromTemplates: async (dealId: string, completeDeal: any, projectResults: any[]) => {
    try {
      // Get user data once at the beginning to avoid multiple calls
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) {
        console.warn('User not authenticated - skipping task creation');
        return;
      }

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile?.organization_id) {
        console.warn('User organization not found - skipping task creation');
        return;
      }

      console.log(`✅ Got user profile (org: ${userProfile.organization_id}), proceeding with task creation`);

      // Use acceptance date as base for task due dates (not current date)
      // This ensures task deadlines remain stable even if the deal is modified later
      const acceptanceDate = new Date(completeDeal.signed_at || completeDeal.closed_at || Date.now());

      const { useServiceTaskTemplateStore } = await import('./serviceTaskTemplateStore');
      const { useProjectStore } = await import('./projectStore');
      
      for (let i = 0; i < completeDeal.deal_services.length; i++) {
        const dealService = completeDeal.deal_services[i];
        const projectResult = projectResults[i];
        
        if (dealService.service_id) {
          try {
            console.log(`🔧 Creating tasks for service ${dealService.service_id}`);
            
            // Get task templates for this service (include sections with is_header=true)
            const { data: templates, error: templatesError } = await supabase
              .from('service_task_templates')
              .select('*, task_location, depends_on_template_id, dependency_type')
              .eq('service_id', dealService.service_id)
              .eq('is_active', true)
              .or('trigger_event.eq.deal_signed,is_header.eq.true')
              .order('task_order')
              
            if (templatesError) {
              console.error('Error fetching templates:', templatesError);
              continue;
            }
            
            if (!templates || templates.length === 0) {
              console.log(`No templates found for service ${dealService.service_id}`);
              continue;
            }
            
            console.log(`\n========== TASK CREATION DEBUG ==========`);
            console.log(`📦 Found ${templates.length} templates for service ${dealService.service_id}`);
            console.log('🔍 All templates (detailed):');
            templates.forEach((t, i) => {
              console.log(`  [${i}] ${t.name}:`);
              console.log(`      - id: ${t.id}`);
              console.log(`      - is_header: ${t.is_header} (type: ${typeof t.is_header})`);
              console.log(`      - task_location: "${t.task_location}" (type: ${typeof t.task_location})`);
              console.log(`      - section: ${t.section}`);
              console.log(`      - trigger_event: ${t.trigger_event}`);
            });

            // Separate templates by location
            // Sezioni (is_header=true), task con task_location='project', E task senza task_location (default) vanno nel progetto
            const projectTemplates = templates.filter(t =>
              t.is_header === true ||
              t.task_location === 'project' ||
              !t.task_location  // Include null/undefined come default al progetto
            );
            // Solo le task ESPLICITAMENTE marcate con task_location='deal' vanno nel deal
            const dealTemplates = templates.filter(t => !t.is_header && t.task_location === 'deal');

            console.log(`\n📊 FILTERING RESULTS:`);
            console.log(`  → projectTemplates (${projectTemplates.length}):`);
            projectTemplates.forEach(t => {
              const reason = t.is_header ? 'is_header=true' : (t.task_location === 'project' ? 'task_location=project' : 'task_location is falsy (default)');
              console.log(`    - "${t.name}" (reason: ${reason})`);
            });
            console.log(`  → dealTemplates (${dealTemplates.length}):`);
            dealTemplates.forEach(t => console.log(`    - "${t.name}"`));
            console.log(`==========================================\n`);
            
            // Create deal/sales activity tasks (only for deal templates)
            if (dealTemplates.length > 0) {
              console.log(`🔧 Creating ${dealTemplates.length} deal/sales activity tasks using RPC`);
              
              // Temporarily update templates to only include deal templates
              // We need to create a custom implementation since the RPC creates all templates
              for (const dealTemplate of dealTemplates) {
                try {
                  console.log(`🔧 Creating deal task from template: ${dealTemplate.name}`);

                  // Calculate due date based on acceptance date (not current date)
                  const dueDate = dealTemplate.trigger_days_offset
                    ? new Date(acceptanceDate.getTime() + dealTemplate.trigger_days_offset * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
                    : null;

                  // Create sales activity for deal task
                  const { data: salesActivity, error: taskError } = await supabase
                    .from('sales_activities')
                    .insert({
                      organization_id: userProfile.organization_id,
                      deal_id: dealId,
                      title: dealTemplate.name,
                      description: dealTemplate.description,
                      type: 'task',
                      status: 'pending',
                      assigned_to: dealTemplate.assigned_user_id,
                      due_date: dueDate ? `${dueDate}T00:00:00.000Z` : new Date().toISOString(),
                      user_id: userData.user.id,
                      metadata: {
                        source_template_id: dealTemplate.id,
                        trigger_days_offset: dealTemplate.trigger_days_offset,
                        source: 'service_task_template'
                      }
                    })
                    .select()
                    .single();

                  if (taskError) {
                    console.error('Error creating deal task:', taskError);
                    continue;
                  }

                  console.log(`✅ Created deal sales activity: ${dealTemplate.name} (ID: ${salesActivity.id})`);
                } catch (dealTaskError) {
                  console.error(`❌ Error creating deal task ${dealTemplate.name}:`, dealTaskError);
                }
              }
            }
            
            // Create project tasks using BATCH INSERT for performance
            console.log(`\n🔍 PROJECT TASK CREATION CHECK:`);
            console.log(`  - projectTemplates.length: ${projectTemplates.length}`);
            console.log(`  - projectResult: ${JSON.stringify(projectResult)}`);
            console.log(`  - projectResult.data: ${projectResult?.data ? JSON.stringify(projectResult.data) : 'undefined'}`);
            console.log(`  - projectResult.data[0]: ${projectResult?.data?.[0] ? JSON.stringify(projectResult.data[0]) : 'undefined'}`);

            if (projectTemplates.length > 0 && projectResult.data && projectResult.data[0]) {
              const projectId = projectResult.data[0].id;

              console.log(`✅ CONDITIONS MET! Creating ${projectTemplates.length} project tasks for project ${projectId} using batch insert`);

              // Separate section headers from actual tasks
              const sectionTemplates = projectTemplates.filter(t => t.is_header === true);
              const taskTemplates = projectTemplates.filter(t => !t.is_header);

              console.log(`📁 Found ${sectionTemplates.length} section templates and ${taskTemplates.length} task templates`);

              // ========== OTTIMIZZAZIONE BATCH ==========

              // 1. Caricare TUTTI i checklist items in una sola query
              const templateIds = taskTemplates.map(t => t.id);
              const { data: allChecklistItems } = templateIds.length > 0
                ? await supabase
                    .from('service_task_template_checklist')
                    .select('*')
                    .in('template_id', templateIds)
                    .order('sort_order')
                : { data: [] };

              // Raggruppa per template_id
              const checklistByTemplate = new Map<string, any[]>();
              (allChecklistItems || []).forEach(item => {
                if (!checklistByTemplate.has(item.template_id)) {
                  checklistByTemplate.set(item.template_id, []);
                }
                checklistByTemplate.get(item.template_id)!.push(item);
              });

              console.log(`📋 Loaded ${allChecklistItems?.length || 0} checklist items for ${checklistByTemplate.size} templates`);

              // Map to store: templateSectionId -> realSectionId
              const sectionMapping = new Map<string, string>();

              // 2. Preparare dati sezioni e inserire in batch
              const sectionInsertData = sectionTemplates.map((st, index) => {
                const realSectionId = `section_${st.name.toLowerCase().replace(/\s+/g, '_')}_${Date.now()}_${index}`;
                if (st.section) {
                  sectionMapping.set(st.section, realSectionId);
                }
                return {
                  project_id: projectId,
                  title: st.name,
                  description: '',
                  status: 'todo',
                  priority: 'medium',
                  is_header: true,
                  section: realSectionId,
                  order: st.sort_order || st.task_order || index,
                  created_by: userData.user.id
                };
              });

              if (sectionInsertData.length > 0) {
                const { error: sectionError } = await supabase.from('tasks').insert(sectionInsertData);
                if (sectionError) {
                  console.error('❌ Error batch inserting sections:', sectionError);
                } else {
                  console.log(`✅ Batch inserted ${sectionInsertData.length} sections`);
                }
              }

              // 3. Preparare dati task e inserire in batch
              // Salviamo anche l'ordine dei template per mappare checklist dopo
              const taskInsertData = taskTemplates.map((template, index) => {
                // Calculate due date based on acceptance date (not current date)
                const dueDate = template.trigger_days_offset
                  ? new Date(acceptanceDate.getTime() + template.trigger_days_offset * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
                  : null;

                const taskSection = template.section && sectionMapping.has(template.section)
                  ? sectionMapping.get(template.section)
                  : null;

                return {
                  project_id: projectId,
                  title: template.name,
                  description: template.description || '',
                  status: 'todo',
                  priority: template.priority || 'medium',
                  assigned_to: template.assigned_user_id || null,
                  due_date: dueDate,
                  section: taskSection,
                  order: template.sort_order || template.task_order || index,
                  created_by: userData.user.id,
                  metadata: {
                    source_template_id: template.id,
                    trigger_days_offset: template.trigger_days_offset,
                    source: 'service_task_template'
                  }
                };
              });

              let createdTasks: any[] = [];
              if (taskInsertData.length > 0) {
                const { data: insertedTasks, error: taskError } = await supabase
                  .from('tasks')
                  .insert(taskInsertData)
                  .select('id');

                if (taskError) {
                  console.error('❌ Error batch inserting tasks:', taskError);
                } else {
                  createdTasks = insertedTasks || [];
                  console.log(`✅ Batch inserted ${createdTasks.length} tasks`);
                }
              }

              // 4. Preparare checklist items e inserire in batch
              // Mappiamo per indice: createdTasks[i] corrisponde a taskTemplates[i]
              const checklistInsertData: any[] = [];
              createdTasks.forEach((task, index) => {
                const template = taskTemplates[index];
                if (!template) return;

                const items = checklistByTemplate.get(template.id) || [];
                items.forEach((item, idx) => {
                  checklistInsertData.push({
                    task_id: task.id,
                    title: item.title,
                    description: item.description || '',
                    is_required: item.is_required || false,
                    sort_order: idx,
                    organization_id: userProfile.organization_id
                  });
                });
              });

              if (checklistInsertData.length > 0) {
                const { error: checklistError } = await supabase.from('task_checklist').insert(checklistInsertData);
                if (checklistError) {
                  console.error('❌ Error batch inserting checklist items:', checklistError);
                } else {
                  console.log(`✅ Batch inserted ${checklistInsertData.length} checklist items`);
                }
              }

              // 5. Trasferire dipendenze da template a task
              // Mappa template ID -> task ID creato
              const templateToTaskMap = new Map<string, string>();
              createdTasks.forEach((task, index) => {
                const template = taskTemplates[index];
                if (template) {
                  templateToTaskMap.set(template.id, task.id);
                }
              });

              // Aggiorna dipendenze per i task che hanno depends_on_template_id
              let dependencyUpdateCount = 0;
              for (const [index, template] of taskTemplates.entries()) {
                if (template.depends_on_template_id) {
                  const taskId = createdTasks[index]?.id;
                  const dependsOnTaskId = templateToTaskMap.get(template.depends_on_template_id);
                  if (taskId && dependsOnTaskId) {
                    const { error: depError } = await supabase
                      .from('tasks')
                      .update({ dependencies: [dependsOnTaskId] })
                      .eq('id', taskId);

                    if (depError) {
                      console.error(`❌ Error setting dependency for task ${taskId}:`, depError);
                    } else {
                      dependencyUpdateCount++;
                    }
                  }
                }
              }
              if (dependencyUpdateCount > 0) {
                console.log(`✅ Set dependencies for ${dependencyUpdateCount} tasks`);
              }

              console.log(`✅ BATCH COMPLETE: Created ${sectionInsertData.length} sections, ${createdTasks.length} tasks, ${checklistInsertData.length} checklist items, ${dependencyUpdateCount} dependencies`);
            } else {
              console.log(`⚠️ CONDITIONS NOT MET for project task creation:`);
              if (projectTemplates.length === 0) {
                console.log(`  → No project templates (all went to deal or none found)`);
              }
              if (!projectResult?.data) {
                console.log(`  → projectResult.data is falsy`);
              }
              if (!projectResult?.data?.[0]) {
                console.log(`  → projectResult.data[0] is falsy`);
              }
            }
          } catch (taskError) {
            console.warn('⚠️ Failed to create tasks from templates:', taskError);
          }
        } else {
          console.warn(`⚠️ Deal service has no service_id - skipping task creation`);
        }
      }
    } catch (taskTemplateError) {
      console.error('❌ Error loading service task template store:', taskTemplateError);
    }
  },

  /**
   * Creates a Deal from a WhatsApp AI suggestion
   * Fetches suggestion details, creates deal with pre-filled data, adds note, updates suggestion status
   */
  createDealFromSuggestion: async (suggestionId: string) => {
    try {
      console.log(`[DealStore] Creating deal from suggestion: ${suggestionId}`);

      const userData = await getUserData();
      if (!userData?.userId) throw new Error("Not authenticated");
      if (!userData?.organizationId) throw new Error("User not associated with an organization");

      // 1. Fetch suggestion
      const { data: suggestion, error: suggestionError } = await supabase
        .from('deal_suggestions')
        .select('*')
        .eq('id', suggestionId)
        .single();

      if (suggestionError) throw suggestionError;
      if (!suggestion) throw new Error('Suggestion not found');

      console.log(`[DealStore] Fetched suggestion:`, suggestion);

      // 2. Fetch client details to get assigned_to
      const { data: client, error: clientError } = await supabase
        .from('clients')
        .select('id, business_name, assigned_to')
        .eq('id', suggestion.client_id)
        .single();

      if (clientError) throw clientError;

      // 3. Create deal with pre-filled data
      const newDeal: NewDeal = {
        client_id: suggestion.client_id,
        title: suggestion.suggested_title,
        description: suggestion.suggested_description || '',
        status: 'prospect',
        closing_probability: suggestion.confidence_score || 50,
        amount: suggestion.estimated_value || 0,
        assigned_to: client?.assigned_to || userData.userId, // Use client's assigned_to or current user
        source: 'whatsapp_ai_suggestion',
        metadata: {
          suggestion_id: suggestionId,
          whatsapp_chat_id: suggestion.whatsapp_chat_id,
          whatsapp_message_id: suggestion.whatsapp_message_id,
          ai_model: suggestion.ai_model,
          confidence_score: suggestion.confidence_score,
          created_from_ai: true
        }
      };

      console.log(`[DealStore] Creating deal with data:`, newDeal);

      // Use the existing createDeal method (no services initially)
      const createdDeal = await get().createDeal(newDeal, []);

      if (!createdDeal) {
        throw new Error('Failed to create deal');
      }

      console.log(`[DealStore] Deal created successfully: ${createdDeal.id}`);

      // 4. Add note with AI reasoning and WhatsApp link
      const noteContent = `🤖 Deal creato automaticamente da suggerimento AI

**Motivo AI:** ${suggestion.ai_reasoning || 'Nessuna motivazione fornita'}

**Confidence Score:** ${suggestion.confidence_score}%

[📱 Vai alla chat WhatsApp](/whatsapp?chat=${suggestion.whatsapp_chat_id})`;

      await get().addDealNote(createdDeal.id, noteContent, {
        source: 'ai_suggestion',
        suggestion_id: suggestionId,
        whatsapp_chat_id: suggestion.whatsapp_chat_id
      });

      console.log(`[DealStore] Added note to deal`);

      // 5. Mark suggestion as accepted
      const { error: updateError } = await supabase
        .from('deal_suggestions')
        .update({
          status: 'accepted',
          created_deal_id: createdDeal.id,
          accepted_at: new Date().toISOString(),
          accepted_by: userData.userId
        })
        .eq('id', suggestionId);

      if (updateError) {
        console.error('[DealStore] Error updating suggestion status:', updateError);
        // Non lanciamo errore qui, il deal è già stato creato
      }

      console.log(`[DealStore] Marked suggestion as accepted`);

      // Emit event for UI updates
      eventBus.emit('deal:created', { dealId: createdDeal.id, source: 'ai_suggestion' });

      return createdDeal;
    } catch (error) {
      console.error('[DealStore] Error creating deal from suggestion:', error);
      throw error;
    }
  },

  // =====================================================
  // HOURS PACKAGE MANAGEMENT (MONTE ORE)
  // =====================================================

  updateHoursPackage: async (dealId: string, hoursPackage: number, alertThreshold?: number, hourlyRate?: number) => {
    try {
      const userData = await getUserData();
      if (!userData.organizationId) {
        throw new Error('Organization ID not found');
      }

      const updateData: Record<string, any> = {
        hours_package: hoursPackage,
        updated_at: new Date().toISOString()
      };

      if (alertThreshold !== undefined) {
        updateData.hours_package_alert_threshold = alertThreshold;
      }
      if (hourlyRate !== undefined) {
        updateData.hourly_rate = hourlyRate;
      }

      const { error } = await supabase
        .from('deals')
        .update(updateData)
        .eq('id', dealId)
        .eq('organization_id', userData.organizationId);

      if (error) throw error;

      // Aggiorna stato locale
      set((state) => ({
        deals: state.deals.map(d =>
          d.id === dealId
            ? {
                ...d,
                hours_package: hoursPackage,
                hours_package_alert_threshold: alertThreshold ?? d.hours_package_alert_threshold,
                hourly_rate: hourlyRate ?? d.hourly_rate
              }
            : d
        )
      }));

      console.log(`[DealStore] Hours package updated for deal ${dealId}: ${hoursPackage}h`);
      return true;
    } catch (error) {
      console.error('[DealStore] Error updating hours package:', error);
      return false;
    }
  },

  getHoursUsagePercent: (dealId: string) => {
    const deal = get().deals.find(d => d.id === dealId);
    if (!deal || !deal.hours_package || deal.hours_package === 0) {
      return null;
    }
    const hoursUsed = deal.hours_used || 0;
    return Math.round((hoursUsed / deal.hours_package) * 100);
  },

  checkHoursAlert: (dealId: string) => {
    const deal = get().deals.find(d => d.id === dealId);
    if (!deal || !deal.hours_package || deal.hours_package === 0) {
      return null;
    }

    const hoursUsed = deal.hours_used || 0;
    const percentUsed = (hoursUsed / deal.hours_package) * 100;
    const threshold = deal.hours_package_alert_threshold || 80;
    const hoursRemaining = deal.hours_package - hoursUsed;

    return {
      isAlert: percentUsed >= threshold,
      percentUsed: Math.round(percentUsed),
      hoursRemaining: Math.max(0, hoursRemaining)
    };
  },

  addHoursToPackage: async (dealId: string, additionalHours: number) => {
    try {
      const userData = await getUserData();
      if (!userData.organizationId) {
        throw new Error('Organization ID not found');
      }

      // Prima ottieni il deal corrente
      const deal = get().deals.find(d => d.id === dealId);
      if (!deal) {
        // Fetch dal database
        const { data: fetchedDeal, error: fetchError } = await supabase
          .from('deals')
          .select('hours_package')
          .eq('id', dealId)
          .eq('organization_id', userData.organizationId)
          .single();

        if (fetchError) throw fetchError;
        if (!fetchedDeal) throw new Error('Deal not found');

        const newTotal = (fetchedDeal.hours_package || 0) + additionalHours;
        return await get().updateHoursPackage(dealId, newTotal);
      }

      const currentHours = deal.hours_package || 0;
      const newTotal = currentHours + additionalHours;

      return await get().updateHoursPackage(dealId, newTotal);
    } catch (error) {
      console.error('[DealStore] Error adding hours to package:', error);
      return false;
    }
  }
}));